import { useEffect, useRef } from 'react';
import { runScopeHighlightIds } from '../../utils/runHighlightScope';
import {
  bayOverrideIdOf,
  dragCounterpart,
} from '../../utils/cabinetRunGeometry';
import { drawFixedMarkers } from '../../utils/fixedCabinetMarker';
import {
  filterObjectsByHeight,
  metersToFeetInches,
} from '../../utils/floorPlanUtils';
import { HIGHLIGHT_COLORS } from '../../constants/highlightColors';
import { getObjectType, drawObjectShape } from '../../utils/objectDrawingUtils';
import {
  drawRoomPolygonPreview,
  drawSnapIndicator,
  drawVertexDeleteOverlay,
  drawVertexAddOverlay,
  drawVertexMoveOverlay,
} from '../../utils/roomDrawingUtils';
import { drawCabinetRunPreview } from '../../utils/cabinetRunDrawingUtils';
import {
  calculateRoomMeasurementsV2,
  shownWallLengthMeasurements,
  drawMeasurementsV2,
  layoutMeasurements,
  measurementInvolvesAnyObject,
} from '../../utils/measurementUtilsV2';
import {
  clearCanvas,
  setupCanvasTransform,
  restoreCanvasTransform,
  beginScreenSpaceOverlay,
} from '../../utils/canvasUtils';
import { apply3DRotationTo2D } from '../../utils/geometryUtils';
import {
  drawWalls,
  drawContextWalls,
  drawWallNormals,
  drawCeilingCoverage,
  drawRoomPolygonOutline,
  drawWindows,
  drawDoorsWithRotation,
  drawOpenings,
  drawDemolishedGhosts,
  drawObjects,
  drawObject,
  drawRoomLabels,
  drawAxes,
  matchesOpeningSelection,
} from '../../utils/elementDrawingUtils';
import {
  buildOpeningLookup,
  resolveRoomOpening,
} from '../../utils/openingSyncUtils';
import { getFreeMoveGroup } from '../../utils/freeMoveGroup';
import {
  matchesStory,
  ghostStoriesFor,
  buildStoryLookup,
  bucketByGhostAlpha,
} from '../../utils/storyOptions';
import { withGhostLayer } from '../../utils/ghostLayer';
import { shouldShowObjectInLayer } from '../../utils/objectVisibility';
import { teaTowelHasNoHandle } from '@shared-lib/staging';
import { collidingObjectIds } from '../../utils/detectRoomCollisions';
import { drawDragClearanceZones } from '../../utils/clearanceDrawing';
import { drawCollisionDebugOverlay } from '../../utils/collisionDebugOverlay';
import { drawResizeHandles } from '../../utils/resizeHandlesUtils';
import {
  drawGrabHandle,
  roomCentroid,
  wallSpanFromEnds,
  drawWallSpanReadout,
  drawReadoutPill,
} from '../../utils/grabHandleUtils';
import { findParentWallInStructure } from '../../utils/parentWallHelpers';
import { formatFeetInches } from '../../utils/dimensionFormat';
import { WALL_THICKNESS } from '../../utils/wallCreationUtils';
import { chainReadoutText } from '../../utils/wallChain';
import { runEndHandles } from '../../utils/runDragMath';
import { cornerHandlesOfSegments } from '../../utils/runCornerMove';
import { freeSegmentDatum } from '../../utils/cabinetRunGeometry';
import {
  grabHandleOfferedFor,
  moveHandleOfferedFor,
} from './runPointerTargets';
import { WALL_ENDPOINT_DRAG_ENABLED } from '../../utils/floorplanEditCapabilities';
import {
  drawWallHandles,
  drawRunEndHandles,
  drawBayEdgeHandles,
  drawFreeSegmentDatum,
  drawFreeSegmentMoveHandle,
} from '../../utils/wallHandlesUtils';
import {
  drawElectricalOverlay,
  collectAllObjects,
  collectAllElectricalData,
} from '../../utils/electricalDrawingUtils';
import { buildWiringRuns } from '../../utils/electricalRuns';
import { drawInspectedElectricalOverlay } from '../../utils/inspectedElectricalDrawing';
import { devicesInRooms } from '../../utils/inspectedElectrical';
import { rowCandidates } from '../../utils/recessedRowPlan';
import {
  drawCalloutPreview,
  drawCalloutsOverlay,
} from '../../utils/calloutDrawingUtils';
import { structureWallsForRoomWalls } from './floorplanDataView';
import { onlyStatedDimensions } from '../../utils/planDimensionRecords';
import { isElectrical } from '../../utils/objectCategoryHelper';
import {
  filterDataToSelectedRoom,
  itemIsWall,
  getElementsWithRoomContext,
} from './floorplanDataView';
import { flattenFloorplanContracts } from './floorplanContracts';
import { STRETCH_PREVIEW_COLORS } from './stretchPreviewColors';
import { highlightFadeAlpha } from '../../utils/highlightFade';
// The object pass hides what the ghost supersedes; the clearance overlay swaps
// in the previewed copies. One module so the two cannot disagree.
import {
  supersededByRunPreview,
  overlayObjectsUnderPreview,
} from './runPreviewObjects';

// How far the plan recedes behind the stated-dimensions layer.
const DIMENSIONS_LAYER_PLAN_ALPHA = 0.28;

// Pure floorplan renderer (Phase 2a). Takes the three contracts
// (model / viewState / editingState) + io, and flattens them back into the
// exact local names the verbatim draw code uses — so drawFloorPlan and the
// draw effect below are byte-identical to the original coordinator code.
export function useFloorplanRenderer(contracts) {
  const {
    structureData,
    forceRedrawCounter,
    scale,
    pan,
    selectedItem,
    showRoomLabels,
    showObjects,
    shouldShowMeasurements,
    rotation,
    selectedRoom,
    selectedStory,
    canvasSize,
    debugMode,
    measurementMode,
    dataSource,
    previewStructure,
    prePreviewStructure,
    stretchBoundary,
    stretchRegion,
    boundaryAxis,
    stretchBoundaryClampReason,
    previewMap,
    lockedKeys,
    collisionTargets,
    overlapWarningTargets,
    showFixtures,
    showElectrical,
    showFurnishings,
    showStaging,
    showDemoedWalls,
    tempResizedObject,
    isResizing,
    activeResizeHandle,
    isDraggingObject,
    isDraggingWall,
    previewObject,
    electricalMode,
    showDimensionsOnly,
    wireShapeDrafts,
    showWiring,
    selectedWiringRunId,
    wiringHitRef,
    connectionMode,
    selectedFloorPlanMeasurement,
    connectionStart,
    showCollisionIndicator,
    takeOffOverlay,
    roomHighlightTick,
    canvasRef,
    measurementsRef,
    measurementFontPx,
    roomDrawing,
    cabinetRunDrawing,
    recessedRowDrawing,
    wallChainDrawing,
    showCallouts,
    selectedCalloutId,
    calloutDrawing,
    calloutHitRef,
    // Where the fixed-cabinet pins were drawn, for the click that releases
    // one (`fixedCabinetMarker`).
    fixedPinHitRef,
    inspectedElectrical,
    appliancePreview,
    runDrag,
    isDraggingRun,
    vertexDelete,
    vertexAdd,
    vertexMove,
    structureWalls,
    showRoomWallsBold,
    objectInteractionMode,
    cabinetEditScope,
    isDraggingWallElement,
    grabHandlesEnabled,
    dragPointer,
    activeWallHandle,
    originalWallState,
    roomHighlightStartedAt,
    debugPanelCollapsed,
    disabledConstraints,
    highlightedElements,
    collisionDebugRef,
    dragRedirectTargetRef,
    selectedAtMouseDownRef,
    canvasResizeHandlesFor,
    drawTakeOffOverlay,
  } = flattenFloorplanContracts(contracts);

  // What `drawFloorPlan` last drew, for the screen-space overlays that run
  // after it in a different scope. Set during the draw, read straight after —
  // never across a frame boundary.
  const drawnObjectsRef = useRef([]);

  // Thin wrappers — same names/signatures the moved drawFloorPlan body uses.
  const getDataToRender = (dataParam) =>
    filterDataToSelectedRoom(dataParam, selectedRoom);
  const isWall = (item) => itemIsWall(item, structureData, structureWalls);

  // Floor-plan drawing — relocated VERBATIM from UVFloorPlanCanvas.js.
  // Body is byte-identical to the original coordinator definition; only
  // its location moved (the renderer now owns its drawing).
  const drawFloorPlan = (ctx, dataToRender, originalData, debugMode) => {
    // Debug log to see what objects we're trying to render
    if (dataToRender?.rooms) {
      const allObjects = dataToRender.rooms.flatMap(
        (room) => room.objects || []
      );
      // Debug logging removed for production
    } else if (dataToRender?.objects) {
      // Direct objects available
    }

    // Build a structure-level opening lookup once per render. The draw
    // functions read opening-itself fields (doorType, slidingPanelCount,
    // wallFacesPriorityRoom) and coords off each iterated item; some of
    // those items are room copies. `resolveForRender` swaps each room
    // copy with a resolver-merged view so the canonical structure values
    // are used. Items without a structureOpeningId (orphan room copies
    // or structure-level inputs) pass through unchanged.
    const structureOpeningLookup = buildOpeningLookup({
      doors: structureData?.structureDoors ?? structureData?.doors,
      windows: structureData?.structureWindows ?? structureData?.windows,
      openings: structureData?.structureOpenings ?? structureData?.openings,
    });
    const resolveForRender = (items) =>
      (items || []).map((item) =>
        item?.structureOpeningId
          ? resolveRoomOpening(
              item,
              structureOpeningLookup.get(item.structureOpeningId)
            )
          : item
      );

    let wallNormals = [];

    // All-floors ghosting inputs (see bucketByGhostAlpha). A picked floor is
    // never ghosted — `ghostStoriesFor` takes `selectedStory` rather than
    // reading the storeys back off the filtered data, which a double-height
    // wall kept from the floor below would otherwise poison.
    const ghostStories = ghostStoriesFor(dataToRender, selectedStory);
    const storyLookup = buildStoryLookup(
      dataToRender?.rooms,
      dataToRender?.walls
    );
    // Draw `paint(ctx2, elements)` once per ghost bucket: upper floors
    // tinted + faded and drawn first, the lowest floor solid on top.
    const paintByStory = (elements, paint) => {
      for (const bucket of bucketByGhostAlpha(
        elements,
        ghostStories,
        storyLookup
      )) {
        withGhostLayer(ctx, { alpha: bucket.alpha }, (layerCtx) =>
          paint(layerCtx, bucket.elements)
        );
      }
    };

    // Store selected wall, door, window, and object for drawing last
    let selectedWall = null;
    let selectedDoor = null;
    let selectedWindow = null;
    let selectedObject = null;
    let selectedOpening = null;

    // Visual selection identity. During a drag with a redirect (vanity
    // child → cabinet, tub child → support-box), selectedItem is the
    // canonical anchor (so the cascade rotates around the right rigid-body
    // pivot). Highlights and focus stay on the user's original click target
    // via this displaySelectedItem fallback so the PRIMARY tier doesn't jump
    // mid-drag.
    const displaySelectedItem =
      isDraggingObject &&
      dragRedirectTargetRef.current &&
      selectedAtMouseDownRef.current?.id &&
      selectedAtMouseDownRef.current.id !== selectedItem?.id
        ? selectedAtMouseDownRef.current
        : selectedItem;

    // Focus set for opacity dimming. When something is selected (an item
    // or a measurement), every other object renders at 0.5 alpha so the
    // focused subset stands out without z-reordering. Null = no dimming.
    //
    // Skip room / roomLabel selections — selecting the room itself isn't
    // a "focus on this one thing" gesture, it's a "scope the view to this
    // room" gesture. If we added the room id to the focus set, every
    // door / window / opening / object inside the room would dim to 0.5
    // alpha (their ids don't match the room's), leaving the whole
    // single-room view looking translucent.
    const inFocusObjectIds = (() => {
      const ids = new Set();
      const isRoomSelection =
        displaySelectedItem?.isRoomLabel ||
        displaySelectedItem?.itemType === 'roomLabel' ||
        displaySelectedItem?.itemType === 'room';
      if (displaySelectedItem?.id && !isRoomSelection) {
        ids.add(displaySelectedItem.id);
        // For openings (door / window / opening), selectedItem comes from
        // interactionUtils' resolver keyed by the slim room copy's id but
        // the render arrays in single-room view and post-dedup elsewhere
        // are the canonical structure-level entries keyed by the structure
        // id. Without adding the structureOpeningId here, the dim check
        // (`!inFocusObjectIds.has(item.id)`) returns true for the
        // canonical entry that matches the selection → the selected
        // opening renders at 0.5 alpha along with everything else, so the
        // highlight looks invisible. Adding both ids in both directions
        // covers id/FK matches without needing FK-aware logic at every
        // dim-check site.
        if (displaySelectedItem?.structureOpeningId) {
          ids.add(displaySelectedItem.structureOpeningId);
        }
      }
      const m = selectedFloorPlanMeasurement;
      if (m?.fromObjectId) ids.add(m.fromObjectId);
      if (m?.toObjectId) ids.add(m.toObjectId);
      (m?.relatedObjectIds || []).forEach((id) => id && ids.add(id));
      return ids.size > 0 ? ids : null;
    })();
    // When a measurement is selected, every party to it (from / to +
    // every fixture-group sibling in relatedObjectIds) should read as
    // highlighted PRIMARY. The from/to-vs-related split exists to
    // distinguish "the object you selected" from "its fixture-group
    // siblings" when an OBJECT is selected — that distinction has no
    // analog when the user selected a MEASUREMENT (everything in the
    // measurement is equally relevant). Previously related ids fell to
    // SECONDARY (blue), which looked wrong for e.g. a vanity whose
    // measurement points at its sub-components: the vanity itself sat
    // in relatedObjectIds and rendered blue while sub-components went
    // pink. Union them into PRIMARY instead; SECONDARY stays empty for
    // measurement-driven selection.
    const measurementPrimaryHighlight = new Set(
      [
        selectedFloorPlanMeasurement?.fromObjectId,
        selectedFloorPlanMeasurement?.toObjectId,
        ...(selectedFloorPlanMeasurement?.relatedObjectIds || []),
      ].filter(Boolean)
    );
    const measurementSecondaryHighlight = new Set();

    // When an object is selected, every member of its move-group paints in
    // SECONDARY (blue) so the user sees exactly what would translate together
    // if they dragged. Uses the same free-move closure (parent-tree
    // descent + fixtureGroupId equivalence) that the drag cascade uses.
    // Group members stay outside inFocusObjectIds so they still dim to 0.5.
    const groupSiblingHighlight = new Set();
    // The REST of the selected box's cabinet run, when the open panel is the
    // RUN's. One scope at a time: the bay panel paints the bay alone and the run
    // panel paints the whole run, both in PRIMARY pink, so the canvas says which
    // of the two panels the next click will change. It used to paint BOTH at
    // once — the bay pink and the run blue — which said the edit target was both
    // (`hooks/useCabinetEditScope`).
    const runScopeHighlight = displaySelectedItem?.id
      ? runScopeHighlightIds({
          rooms: structureData?.rooms,
          selectedObject: displaySelectedItem,
          cabinetEditScope,
        })
      : new Set();
    if (displaySelectedItem?.id && structureData?.rooms) {
      structureData.rooms.forEach((room) => {
        if (!room.objects?.some((o) => o?.id === displaySelectedItem.id))
          return;
        // The same set the drag would translate — including its refusal to
        // carry a run's cabinetry — so the blue says what will actually move.
        const moveGroup = getFreeMoveGroup(displaySelectedItem.id, room);
        moveGroup.forEach((id) => {
          if (id !== displaySelectedItem.id) groupSiblingHighlight.add(id);
        });
      });
    }
    const objectSecondaryHighlight = new Set([
      ...measurementSecondaryHighlight,
      ...groupSiblingHighlight,
    ]);
    // Everything that paints PRIMARY without BEING the selection: the parties to
    // a selected measurement, and the rest of the run while the run panel is
    // open. In the focus set too — a run member dimmed to 0.5 while it is the
    // edit target would read as the background it is not.
    const objectPrimaryHighlight = new Set([
      ...measurementPrimaryHighlight,
      ...runScopeHighlight,
    ]);
    if (inFocusObjectIds)
      runScopeHighlight.forEach((id) => inFocusObjectIds.add(id));

    // Check if we have room-based data structure
    const hasRoomStructure =
      dataToRender.rooms &&
      dataToRender.rooms.length > 0 &&
      dataToRender.rooms.some(
        (room) => room.walls || room.objects || room.doors || room.windows
      );

    // ONE flags bag for every layer test below. It was hand-built three times
    // here and once in UVFloorPlanCanvas; adding a layer then meant editing
    // four identical objects, and a missed one fails SILENTLY (the predicate
    // reads `!== false`, so an absent flag means visible).
    const layerFlags = {
      showElectrical,
      showFixtures,
      showFurnishings,
      showStaging,
      electricalMode,
    };

    // Filter objects based on fixtures and furnishings toggles
    let objectsToDisplay = [];

    // If we have room structure, get objects with room context
    if (hasRoomStructure && dataToRender.rooms) {
      const allObjectsWithContext = getElementsWithRoomContext(
        dataToRender.rooms,
        'objects'
      );

      // Filter based on fixtures, electrical, and furnishings toggles
      objectsToDisplay = allObjectsWithContext.filter(
        (obj) =>
          shouldShowObjectInLayer(obj, layerFlags) &&
          !teaTowelHasNoHandle(obj, allObjectsWithContext)
      );

      // In 'both' mode, also include structure-level objects with special marker
      if (
        dataSource === 'both' &&
        dataToRender.objects &&
        dataToRender.objects.length > 0
      ) {
        const structureObjects = dataToRender.objects
          .map((obj) => ({
            ...obj,
            _isStructureLevel: true,
          }))
          .filter((obj) => shouldShowObjectInLayer(obj, layerFlags));
        objectsToDisplay = [...objectsToDisplay, ...structureObjects];
      }
    } else if (dataToRender.objects && dataToRender.objects.length > 0) {
      // Fallback to flattened array (for structure mode or backward compatibility)
      objectsToDisplay = dataToRender.objects.filter((obj) =>
        shouldShowObjectInLayer(obj, layerFlags)
      );
    }

    // The wiring overlay draws AFTER this, in screen space, from a different
    // scope — so hand it the objects that were actually drawn. Deriving the
    // set twice is how a wire ends up running to a fixture the current room
    // isolation has hidden.
    drawnObjectsRef.current = objectsToDisplay;

    // Offset shower doors by their parent shower's clearanceOffset
    // so the door visually follows the access opening
    objectsToDisplay = objectsToDisplay.map((obj) => {
      if (obj.type === 'shower-door' && obj.fixtureGroupId) {
        // Find parent shower in the same fixture group
        const parentShower = objectsToDisplay.find(
          (o) => o.fixtureGroupId === obj.fixtureGroupId && o.type === 'shower'
        );
        if (parentShower?.clearanceOffset) {
          const rot = parentShower.rotation?.y || 0;
          const wallDirX = Math.cos(rot);
          const wallDirZ = -Math.sin(rot);
          return {
            ...obj,
            center: {
              ...obj.center,
              x: obj.center.x + wallDirX * parentShower.clearanceOffset,
              z: obj.center.z + wallDirZ * parentShower.clearanceOffset,
            },
          };
        }
      }
      return obj;
    });

    // Mid-drag swap (mirrors the door / window / opening swap further up).
    // tempResizedObject covers the resize path; isDraggingObject + selectedItem
    // covers the move path. Without this the object body draws at its old
    // position throughout the drag — only the parent's selectedItem state
    // updates each mousemove.
    if (
      isDraggingObject &&
      selectedItem &&
      !isWall(selectedItem) &&
      !selectedItem.category?.door &&
      !selectedItem.category?.window &&
      !selectedItem.category?.opening &&
      selectedItem.id
    ) {
      objectsToDisplay = objectsToDisplay.map((obj) =>
        obj.id === selectedItem.id
          ? {
              ...obj,
              center: selectedItem.center,
              rotation: selectedItem.rotation,
              dimensions: selectedItem.dimensions,
            }
          : obj
      );
    }

    // Validate placement of fix-all-actionable fixtures and mark collisions.
    // Scope + group-propagation logic lives in the shared util so the canvas,
    // CollisionIndicator pill, and admin debug all report the same set.
    // Gated by `showCollisionIndicator` so callers (e.g. LayoutsModal previews)
    // can hide the red outline+hatching from non-admin users.
    if (
      showCollisionIndicator &&
      objectsToDisplay.length > 0 &&
      dataToRender.rooms
    ) {
      const room =
        selectedRoom ||
        (dataToRender.rooms.length === 1 ? dataToRender.rooms[0] : null);
      if (room) {
        const collidingIds = collidingObjectIds(
          {
            walls: room.walls || [],
            doors: room.doors || [],
            windows: room.windows || [],
            openings: room.openings || [],
            objects: room.objects || [],
            floors: room.floors || [],
            disabledConstraints: room.disabledConstraints || [],
          },
          structureData
        );
        if (collidingIds.size > 0) {
          objectsToDisplay = objectsToDisplay.map((obj) =>
            collidingIds.has(obj.id) ? { ...obj, hasCollision: true } : obj
          );
        }
      }
    }
    drawnObjectsRef.current = objectsToDisplay;

    // Draw highlighted room floor first (so it appears under other elements).
    // It holds, then fades, on the same timing as the 3D viewer's selection
    // pink (`utils/highlightFade`).
    if (selectedItem && selectedItem.isRoomLabel && selectedItem.floors) {
      const alpha =
        roomHighlightStartedAt == null
          ? 1
          : highlightFadeAlpha(Date.now() - roomHighlightStartedAt);
      if (alpha > 0) {
        ctx.save();
        ctx.globalAlpha = alpha;

        selectedItem.floors.forEach((floor) => {
          if (
            floor &&
            floor.polygonCorners &&
            floor.polygonCorners.length > 0
          ) {
            ctx.fillStyle = HIGHLIGHT_COLORS.FLOOR_FILL;
            ctx.strokeStyle = HIGHLIGHT_COLORS.FLOOR_STROKE;
            ctx.lineWidth = HIGHLIGHT_COLORS.EXTRA_THICK_LINE(scale);

            ctx.beginPath();
            floor.polygonCorners.forEach((point, i) => {
              const x = point.x;
              const y = point.z;

              if (i === 0) {
                ctx.moveTo(x, y);
              } else {
                ctx.lineTo(x, y);
              }
            });
            ctx.closePath();

            ctx.fill();
            ctx.stroke();
          }
        });

        ctx.restore();
      }
    }

    // Get canvas dimensions from context
    const canvas = ctx.canvas;

    // Draw coordinate axes last (so they appear on top)
    // Note: drawAxes handles its own screen transformation

    // Get all elements with room context if available
    let wallsToRender = [];
    let doorsForWalls = [];
    let windowsForWalls = [];
    let openingsForWalls = [];
    // Room copies saved before dedup (for debug visualization)
    let roomDoorCopies = [];
    let roomWindowCopies = [];
    let roomOpeningCopies = [];

    if (hasRoomStructure && dataToRender.rooms) {
      wallsToRender = getElementsWithRoomContext(dataToRender.rooms, 'walls');
      // Collect doors, windows, and openings for wall gap calculation
      doorsForWalls = getElementsWithRoomContext(dataToRender.rooms, 'doors');
      windowsForWalls = getElementsWithRoomContext(
        dataToRender.rooms,
        'windows'
      );
      openingsForWalls = getElementsWithRoomContext(
        dataToRender.rooms,
        'openings'
      );

      // Structure-level openings are canonical — prefer them over room copies.
      // Build structure ID sets first, then keep room-level only as fallback.
      const structureDoorIds = new Set(
        (dataToRender.doors || []).map((d) => d.id)
      );
      const structureWindowIds = new Set(
        (dataToRender.windows || []).map((w) => w.id)
      );
      const structureOpeningIds = new Set(
        (dataToRender.openings || []).map((o) => o.id)
      );

      // Save room copies before filtering (for debug visualization)
      roomDoorCopies = doorsForWalls.filter(
        (d) => d.structureOpeningId || structureDoorIds.has(d.id)
      );
      roomWindowCopies = windowsForWalls.filter(
        (w) => w.structureOpeningId || structureWindowIds.has(w.id)
      );
      roomOpeningCopies = openingsForWalls.filter(
        (o) => o.structureOpeningId || structureOpeningIds.has(o.id)
      );

      // Remove room doors that have a structure counterpart (same ID or structureOpeningId link)
      doorsForWalls = doorsForWalls.filter(
        (d) => !d.structureOpeningId && !structureDoorIds.has(d.id)
      );
      windowsForWalls = windowsForWalls.filter(
        (w) => !w.structureOpeningId && !structureWindowIds.has(w.id)
      );
      openingsForWalls = openingsForWalls.filter(
        (o) => !o.structureOpeningId && !structureOpeningIds.has(o.id)
      );

      if (dataSource === 'both') {
        // When a room is selected, skip adding structure-level walls so only
        // that room's walls render (avoids orphaned structure walls persisting)
        if (!selectedRoom) {
          // Add structure-level walls with special marker for purple rendering
          if (dataToRender.walls && dataToRender.walls.length > 0) {
            const structureWalls = dataToRender.walls.map((wall) => ({
              ...wall,
              _isStructureLevel: true,
            }));
            wallsToRender = [...wallsToRender, ...structureWalls];
          }
        }
        // Add all structure-level doors/windows/openings with marker
        if (dataToRender.doors && dataToRender.doors.length > 0) {
          doorsForWalls = [
            ...doorsForWalls,
            ...dataToRender.doors.map((d) => ({
              ...d,
              _isStructureLevel: true,
            })),
          ];
        }
        if (dataToRender.windows && dataToRender.windows.length > 0) {
          windowsForWalls = [
            ...windowsForWalls,
            ...dataToRender.windows.map((w) => ({
              ...w,
              _isStructureLevel: true,
            })),
          ];
        }
        if (dataToRender.openings && dataToRender.openings.length > 0) {
          openingsForWalls = [
            ...openingsForWalls,
            ...dataToRender.openings.map((o) => ({
              ...o,
              _isStructureLevel: true,
            })),
          ];
        }
      } else {
        // Add all structure-level doors/windows/openings
        if (dataToRender.doors && dataToRender.doors.length > 0) {
          doorsForWalls = [...doorsForWalls, ...dataToRender.doors];
        }
        if (dataToRender.windows && dataToRender.windows.length > 0) {
          windowsForWalls = [...windowsForWalls, ...dataToRender.windows];
        }
        if (dataToRender.openings && dataToRender.openings.length > 0) {
          openingsForWalls = [...openingsForWalls, ...dataToRender.openings];
        }
      }
    } else if (
      dataToRender.walls ||
      dataToRender.doors ||
      dataToRender.windows
    ) {
      // Use flattened arrays (for structure mode)
      wallsToRender = dataToRender.walls || [];
      doorsForWalls = dataToRender.doors || [];
      windowsForWalls = dataToRender.windows || [];
      openingsForWalls = dataToRender.openings || [];
    }

    // When the user is mid-drag of a door / window / opening, swap the
    // matching entry in doorsForWalls / windowsForWalls / openingsForWalls
    // with the live `selectedItem` (which the drag handler updates each
    // mousemove). Every downstream consumer — drawWalls (which carves the
    // wall gap), drawDoorsWithRotation (door body + arc), drawWindows,
    // drawOpenings — then sees one consistent dragged position instead of
    // the previous split where only a separate semi-transparent rectangle
    // overlay followed the mouse and the body / arc / wall-gap stayed at
    // the original position. Match on id, and also on structureOpeningId in
    // either direction so the swap works whether selectedItem references a
    // structure-level canonical entry or a slim room copy. Object drags
    // already use the same pattern via tempResizedObject below.
    if (
      isDraggingWallElement &&
      selectedItem &&
      (selectedItem.category?.door ||
        selectedItem.category?.window ||
        selectedItem.category?.opening)
    ) {
      const matchesDragged = (item) => {
        if (!item) return false;
        if (item.id === selectedItem.id) return true;
        if (
          selectedItem.structureOpeningId &&
          item.id === selectedItem.structureOpeningId
        )
          return true;
        if (
          item.structureOpeningId &&
          item.structureOpeningId === selectedItem.id
        )
          return true;
        return false;
      };
      const applyDrag = (arr) =>
        arr.map((item) =>
          matchesDragged(item)
            ? {
                ...item,
                center: selectedItem.center,
                dimensions: selectedItem.dimensions,
                rotation: selectedItem.rotation,
              }
            : item
        );
      if (selectedItem.category?.door) {
        doorsForWalls = applyDrag(doorsForWalls);
      } else if (selectedItem.category?.window) {
        windowsForWalls = applyDrag(windowsForWalls);
      } else if (selectedItem.category?.opening) {
        openingsForWalls = applyDrag(openingsForWalls);
      }
    }

    // Always draw walls if they exist
    if (wallsToRender && wallsToRender.length > 0) {
      // Find selected wall first
      if (selectedItem && isWall(selectedItem)) {
        // First try to find in room walls
        selectedWall = wallsToRender.find(
          (wall) => wall.id && selectedItem.id && wall.id === selectedItem.id
        );

        // If not found in room walls, try structure walls
        if (!selectedWall && structureWalls?.length > 0) {
          selectedWall = structureWalls.find(
            (sw) => sw.id && selectedItem.id && sw.id === selectedItem.id
          );
        }

        // If found, use the wall data from the source as it has the latest data from Firebase
        if (selectedWall) {
          // Merge selectedItem flags but keep the wall data from wallsToRender/structureWalls
          selectedWall = {
            ...selectedWall,
            // Preserve selection state
            _isSelected: true,
          };
        } else {
          // If still not found, use the selectedItem itself as it contains wall data
          selectedWall = selectedItem;
        }
      }

      // First pass: draw all walls
      // When dragging a wall, replace it with the temp edited version in the collection
      let wallsToDrawFirstPass = wallsToRender;

      if (
        selectedWall &&
        (isDraggingWall || (isDraggingObject && selectedItem?.isWall)) &&
        selectedItem // Use selected wall directly
      ) {
        // When dragging (either endpoints or whole wall), replace the selected wall with the temp edited version
        wallsToDrawFirstPass = wallsToRender.map((wall) => {
          const isSelectedWall = wall.id === selectedWall.id;
          // Use the selected item as-is, preserving its thickness
          if (isSelectedWall && selectedItem) {
            // Don't modify thickness - keep original even if zero
            return selectedItem;
          }
          return wall;
        });
      } else if (selectedWall && !isDraggingWall) {
        // When not dragging, replace selected wall if it's been edited, or exclude it for second pass
        if (selectedItem && selectedItem.isWallEdited) {
          // Replace the wall in the collection with the edited version
          wallsToDrawFirstPass = wallsToRender.map((wall) => {
            const isSelectedWall = wall.id === selectedWall.id;
            return isSelectedWall ? selectedWall : wall;
          });
        } else {
          // Exclude selected wall (it will be drawn in second pass with highlighting)
          wallsToDrawFirstPass = wallsToRender.filter((wall) => {
            const isSelectedWall = wall.id === selectedWall.id;
            return !isSelectedWall;
          });
        }
      }

      // Get wall extension metadata from structure data if available
      const wallExtensionMetadata =
        structureData?.wallThickness?.wallExtensionMetadata || {};

      // When a room is selected, only pass structure walls associated with
      // that room's walls so orphaned structure walls don't render
      let structureWallsForDrawing = structureWalls;
      // Structure walls from OTHER rooms that are near the selected room —
      // drawn faded for context (see drawContextWalls).
      let contextWalls = [];
      if (selectedRoom && structureWalls?.length > 0) {
        structureWallsForDrawing = structureWallsForRoomWalls(
          structureWalls,
          wallsToRender
        );
        const associatedIds = new Set(
          structureWallsForDrawing.map((sw) => sw.id)
        );
        // Stacked stories sit at the same X/Z (placeStory lifts Y only), so
        // the distance fade alone would draw other floors' walls as context.
        // Keep context to the selected room's own story.
        const roomStory = Number.isFinite(selectedRoom.story)
          ? selectedRoom.story
          : null;
        contextWalls = structureWalls.filter(
          (sw) => !associatedIds.has(sw.id) && matchesStory(sw, roomStory)
        );
      }

      // When dragging a structure wall, replace it with the updated position
      if (isDraggingWall && selectedItem?._isStructureWall) {
        structureWallsForDrawing = structureWallsForDrawing.map((sw) =>
          sw.id === selectedItem.id ? selectedItem : sw
        );
      }

      // Faded context: nearby structure walls from other rooms, drawn first so
      // the selected room's own (solid) walls render on top. Only in bold mode
      // (where structure walls are shown at all) and single-room view.
      if (showRoomWallsBold && selectedRoom && contextWalls.length > 0) {
        drawContextWalls(
          ctx,
          contextWalls,
          selectedRoom.floors?.[0]?.polygonCorners || [],
          scale
        );
      }

      // Hydrate slim room-side opening copies (post-PR #634: id + FK +
      // parentWallIds only) against the structure-side canonical
      // record. drawWalls' `getOpeningsOnWall` / `getDoorsOnWall` /
      // `getWindowsOnWall` filter out anything without `center`, so
      // un-hydrated slim copies were silently dropped — the wall
      // rendered solid with no gap (no white interior on doors /
      // windows / openings).
      const hydratedWindows = resolveForRender(windowsForWalls);
      const hydratedDoors = resolveForRender(doorsForWalls);
      const hydratedOpenings = resolveForRender(openingsForWalls);

      // All-floors view of a stacked structure: upper stories are ghosted
      // by alpha and drawn first, the lowest story solid on top. A picked
      // floor (or a single-story structure) yields one solid bucket.
      wallNormals = [];
      paintByStory(wallsToDrawFirstPass, (layerCtx, elements) => {
        const normals = drawWalls(
          layerCtx,
          elements,
          scale,
          isDraggingWall ? selectedItem : null, // Pass selectedItem when dragging for highlighting
          debugMode,
          dataToRender.rooms || [],
          hydratedWindows,
          hydratedDoors,
          hydratedOpenings,
          wallExtensionMetadata, // Pass wall extension metadata
          showRoomWallsBold, // Pass room walls bold flag
          structureWallsForDrawing, // Pass structure walls (with updated position if dragging)
          showDemoedWalls, // Pass demoed-wall visibility flag
          Boolean(selectedRoom) // Only fade wall overhang in single-room view
        );
        if (Array.isArray(normals)) wallNormals.push(...normals);
      });
    }

    // Room polygon outline removed - no longer needed when room is selected

    // Debug-only: pink shade over each room's persisted ceiling coverage.
    // Areas of the floor polygon WITHOUT pink are rooms / sub-regions
    // missing a ceiling. Renders after walls so it sits visibly on top.
    if (debugMode && dataToRender.rooms) {
      dataToRender.rooms.forEach((room) => {
        if (Array.isArray(room.ceilings)) {
          drawCeilingCoverage(ctx, room.ceilings, scale);
        }
      });
    }

    // In debug mode, draw floor polygons for all rooms
    if (debugMode && dataToRender.rooms) {
      dataToRender.rooms.forEach((room) => {
        if (room.floors && room.floors[0] && room.floors[0].polygonCorners) {
          drawRoomPolygonOutline(
            ctx,
            room,
            room.floors[0].polygonCorners,
            scale
          );
        }
      });
    }

    // Also draw structure-level floor polygons if they exist
    // Only draw these when dataSource includes structure data AND debug mode is on
    if (debugMode && dataToRender.floors && dataSource === 'structure') {
      dataToRender.floors.forEach((floor, index) => {
        if (floor && floor.polygonCorners) {
          // Log the first few points to debug scale issues
          if (debugMode && index === 0) {
            //   'Structure floor polygon points (first 3):',
            //   floor.polygonCorners.slice(0, 3).map((p) => ({ x: p.x, z: p.z }))
            // );

            // Calculate bounds to understand scale
            const bounds = floor.polygonCorners.reduce(
              (acc, point) => {
                return {
                  minX: Math.min(acc.minX, point.x),
                  maxX: Math.max(acc.maxX, point.x),
                  minZ: Math.min(acc.minZ, point.z),
                  maxZ: Math.max(acc.maxZ, point.z),
                };
              },
              {
                minX: Infinity,
                maxX: -Infinity,
                minZ: Infinity,
                maxZ: -Infinity,
              }
            );

            //   width: bounds.maxX - bounds.minX,
            //   height: bounds.maxZ - bounds.minZ,
            //   bounds,
            // });
          }

          // Draw structure floor polygon with a different style
          ctx.save();
          ctx.strokeStyle = debugMode ? 'purple' : '#8B00FF'; // Purple for structure floors
          ctx.lineWidth = Math.max(0.03, 3 / scale); // Scale line width like room polygons
          ctx.setLineDash([
            Math.max(0.15, 10 / scale),
            Math.max(0.075, 5 / scale),
          ]); // Scale dash pattern

          // Don't apply scale here - the canvas transform already handles it
          // Just draw in world coordinates like room polygons do
          ctx.beginPath();
          floor.polygonCorners.forEach((point, i) => {
            // COORDINATE MAPPING: UV Floor Plan Canvas
            // Canvas X = 3D X (horizontal, no transformation)
            // Canvas Y = 3D Z (depth, no negation needed)
            // 3D Y (height) is not displayed in floor plan view
            const x = point.x;
            const y = point.z; // Direct mapping: 3D Z → Canvas Y

            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          });
          ctx.closePath();
          ctx.stroke();

          // Add label if in debug mode
          if (debugMode && floor.polygonCorners.length > 0) {
            ctx.fillStyle = 'purple';
            ctx.font = `${12 / scale}px Arial`; // Scale font size
            const labelX = floor.polygonCorners[0].x + 10 / scale;
            const labelY = floor.polygonCorners[0].z - 10 / scale;
            ctx.fillText(`Structure Floor ${index}`, labelX, labelY);
          }

          ctx.restore();
        }
      });
    }

    // Reuse the already-deduped doorsForWalls array for rendering
    // (dedup logic above filters room copies when structure counterparts exist)
    let doorsToRender = doorsForWalls;

    // Draw doors (only from the filtered data)
    if (doorsToRender && doorsToRender.length > 0) {
      // Find selected door. FK-aware match: doorsToRender holds the
      // canonical structure-level entries (post-dedup), but selectedItem
      // is keyed by the slim room copy's id. Strict id-equals would
      // miss every time → selectedDoor=undefined → the wall-redraw
      // block at line ~7423 never runs → no wall-gap pink fill.
      if (selectedItem && selectedItem.category?.door) {
        selectedDoor = doorsToRender.find((door) =>
          matchesOpeningSelection(door, selectedItem)
        );
      }

      // All doors should use the new format with center/rotation
      // Always use drawDoorsWithRotation
      paintByStory(resolveForRender(doorsToRender), (layerCtx, doors) =>
        drawDoorsWithRotation(
          layerCtx,
          doors,
          scale,
          selectedItem,
          debugMode,
          wallsToRender || [],
          showRoomWallsBold,
          structureWalls,
          objectPrimaryHighlight,
          objectSecondaryHighlight,
          inFocusObjectIds
        )
      );
    }

    // Reuse the already-deduped windowsForWalls array for rendering
    let windowsToRender = windowsForWalls;

    // Draw windows if they exist (only from the filtered data)
    if (windowsToRender && windowsToRender.length > 0) {
      // Find selected window. Same FK-aware match as the door block
      // above — see comment there.
      if (selectedItem && selectedItem.category?.window) {
        selectedWindow = windowsToRender.find((window) =>
          matchesOpeningSelection(window, selectedItem)
        );
      }

      paintByStory(resolveForRender(windowsToRender), (layerCtx, windows) =>
        drawWindows(
          layerCtx,
          windows,
          scale,
          selectedItem,
          debugMode,
          wallsToRender,
          doorsForWalls,
          showRoomWallsBold,
          structureWalls,
          objectPrimaryHighlight,
          objectSecondaryHighlight,
          inFocusObjectIds
        )
      );
    }

    // Reuse the already-deduped openingsForWalls array for rendering
    let openingsToRender = openingsForWalls;

    // Store selected opening for later
    if (openingsToRender.length > 0) {
      // Find selected opening. Same FK-aware match as the door / window
      // blocks above. Critical for cased openings + demoed walls
      // because they have NO body render — the wall-gap pink fill
      // (drawn inside the wall-redraw block below) is their ONLY
      // highlight path.
      if (selectedItem && selectedItem.category?.opening) {
        selectedOpening = openingsToRender.find((opening) =>
          matchesOpeningSelection(opening, selectedItem)
        );
      }
    }

    // Clear hidden demoed-wall spans to open space BEFORE drawing objects so
    // room objects sit on top of the cleared strip (the black end caps are
    // drawn after objects by the drawOpenings call further below).
    if (openingsToRender.length > 0) {
      drawOpenings(
        ctx,
        resolveForRender(openingsToRender),
        scale,
        null,
        debugMode,
        wallsToRender || [],
        showRoomWallsBold,
        structureWalls,
        showDemoedWalls,
        true // clearDemoedOnly
      );
    }

    // The pins a click can release are this frame's: a frame that draws no
    // objects draws no pin, and must not leave the last frame's clickable.
    if (fixedPinHitRef) fixedPinHitRef.current = [];

    // Draw objects if enabled
    if (showObjects && objectsToDisplay.length > 0) {
      // Find selected object. Doors and windows have their own per-side
      // dimension editors in the properties panel and don't take part in
      // canvas-drag resize; plain openings and demoed walls do — they
      // need `selectedObject` set so the resize-handle draw block
      // (~7955, gated on `selectedObject`) renders their handles.
      if (
        selectedItem &&
        !selectedItem.category?.door &&
        !selectedItem.category?.window &&
        !isWall(selectedItem) &&
        !selectedItem.isRoomLabel
      ) {
        selectedObject = selectedItem;
      }

      // Single-pass render. If we're mid-resize, swap the selected object
      // for its tempResizedObject preview. selectionHighlightColor only
      // overrides on collision (red); valid moves keep the normal highlight.
      const tempObjectForRender = tempResizedObject;
      const objectsForRenderBase =
        selectedObject && tempObjectForRender
          ? objectsToDisplay.map((obj) =>
              obj.id === selectedItem.id ? tempObjectForRender : obj
            )
          : objectsToDisplay;
      // While a run preview is live (an appliance drop, a run-end or bay
      // drag), hide the run's current objects — and anything the preview
      // names in `hideIds` (a dropped appliance on an auto run carries no
      // runId) — so the ghosted re-solve doesn't double up with them.
      const objectsForRender = appliancePreview
        ? objectsForRenderBase.filter(
            (o) => !supersededByRunPreview(appliancePreview, o)
          )
        : objectsForRenderBase;
      const isMoving =
        objectInteractionMode === 'move' ||
        objectInteractionMode === 'move-group';
      const selectionHighlightColor =
        isMoving && selectedObject && selectedItem?.hasCollision
          ? '#ef4444'
          : null;
      // The dimensions layer lightens what it draws over. A shower, a tub or a
      // vanity is a solid dark block at plan scale, and a dimension crossing
      // one is unreadable — so the plan recedes rather than the dimensions
      // fighting it. Everything still draws: this hides nothing.
      //
      // ELECTRICAL fixtures keep full strength. They are what these
      // dimensions are usually about — a light centred in a room, an outlet
      // off a doorway — so fading them would dim the subject along with the
      // background. Split into two passes rather than one: `drawObjects`
      // still gets the whole structure as context, only the list it paints
      // is narrowed.
      const fadePlan = !!showDimensionsOnly;
      const planObjects = fadePlan
        ? objectsForRender.filter((o) => !isElectrical(o))
        : objectsForRender;

      const objectDrawOptions = {
        highlightColor: selectionHighlightColor,
        highlightObjectIds: objectPrimaryHighlight,
        secondaryHighlightObjectIds: objectSecondaryHighlight,
        inFocusObjectIds,
        showDemoedWalls, // main e82f68225: hatch/hide demoed objects per demo toggle
        electricalMode,
      };
      const paintObjects = (list) =>
        paintByStory(list, (layerCtx, objects) =>
          drawObjects(
            layerCtx,
            objects,
            originalData,
            scale,
            displaySelectedItem,
            debugMode,
            objectDrawOptions
          )
        );

      if (fadePlan) ctx.globalAlpha = DIMENSIONS_LAYER_PLAN_ALPHA;
      paintObjects(planObjects);
      // A pin on each cabinet the user fixed — what a dragged neighbour stops
      // against, and a click on it lets the cabinet go (`fixedCabinetMarker`).
      // Not on the electrical drawing. Recorded where drawn, so only a pin on
      // screen can be clicked.
      const pinHits = electricalMode
        ? []
        : drawFixedMarkers(ctx, planObjects, scale);
      if (fixedPinHitRef) fixedPinHitRef.current = pinHits;
      if (fadePlan) {
        // The electrical fixtures, at full strength, over the faded plan.
        ctx.globalAlpha = 1;
        paintObjects(objectsForRender.filter(isElectrical));
      }

      // Ghosted preview of the re-solved run during an appliance drop, a
      // run-end drag or a bay drag. Positions are clamped to valid spots, so
      // the ghost always shows a placeable layout.
      if (appliancePreview?.objects?.length) {
        ctx.save();
        ctx.globalAlpha = 0.5;
        // The dragged piece stays SELECTED in the ghost: the re-solve gave it
        // a new id, and without its stand-in nothing said what was moving
        // (`dragCounterpart`, user report 2026-10-26).
        drawObjects(
          ctx,
          appliancePreview.objects,
          originalData,
          scale,
          isDraggingRun
            ? dragCounterpart(appliancePreview.objects, selectedItem)
            : null,
          debugMode,
          { electricalMode }
        );
        ctx.restore();
        if (!electricalMode) {
          drawFixedMarkers(ctx, appliancePreview.objects, scale);
        }
      }
    }

    if (showDimensionsOnly) ctx.globalAlpha = 1;

    // Objects (including bathroom fixtures) are already drawn above via drawObjects

    // Draw room labels if enabled
    if (showRoomLabels && dataToRender.rooms && dataToRender.rooms.length > 0) {
      // Upper-floor labels ghost with their walls in the all-floors view.
      paintByStory(dataToRender.rooms, (layerCtx, rooms) =>
        drawRoomLabels(layerCtx, rooms, originalData, debugMode)
      );
    }

    // Draw openings AFTER objects and labels for better visibility (debugging)
    if (openingsToRender.length > 0) {
      paintByStory(resolveForRender(openingsToRender), (layerCtx, openings) =>
        drawOpenings(
          layerCtx,
          openings,
          scale,
          null,
          debugMode,
          wallsToRender || [],
          showRoomWallsBold,
          structureWalls,
          showDemoedWalls
        )
      );
    }

    // Ghost-render the demolished bucket (structure.demolished) as hatched
    // walls — these were removed from the active walls, so drawWalls/drawOpenings
    // above never touch them. Gated on showDemoedWalls (OFF → open floor).
    drawDemolishedGhosts(
      ctx,
      structureData?.demolished,
      scale,
      selectedItem,
      showDemoedWalls
    );

    // Draw wall normals last so they appear on top (only in debug mode)
    if (debugMode && wallNormals.length > 0) {
      drawWallNormals(ctx, wallNormals, scale, debugMode);
    }

    // Exterior walls don't appear in the main canvas render — only the
    // 3D view paints them. In debug mode, draw a purple arrow at every
    // exterior wall's center perpendicular to its rotation so its
    // stored position is visible alongside the green room-wall arrows.
    if (debugMode) {
      const exteriorWalls = dataToRender?.exterior?.walls || [];
      if (exteriorWalls.length > 0) {
        const exteriorNormals = [];
        for (const w of exteriorWalls) {
          if (!w?.center || !w?.dimensions || !w?.rotation) continue;
          const dir = apply3DRotationTo2D(w.rotation);
          const perpDir = [-dir[1], dir[0]];
          exteriorNormals.push({
            centerX: w.center.x,
            centerZ: w.center.z,
            perpDir,
            thickness: w.dimensions.z,
            alignmentStatus: 'exterior',
          });
        }
        if (exteriorNormals.length > 0) {
          drawWallNormals(ctx, exteriorNormals, scale, debugMode, '#a855f7');
        }
      }
    }

    // Draw debug markers for ALL opening copies (structure + room) when debug mode is on
    if (debugMode) {
      const debugRadius = 0.06; // 6cm radius
      const labelOffset = 0.12; // label offset from center
      const fontSize = Math.max(0.06, 8 / scale);

      const drawCopyMarker = (el, type, isStructure) => {
        if (!el.center) return;
        const cx = el.center.x;
        const cz = el.center.z;

        ctx.save();

        // Structure copies: blue circle, room copies: orange circle
        if (isStructure) {
          ctx.fillStyle = 'rgba(59, 130, 246, 0.7)'; // blue
          ctx.strokeStyle = '#2563eb';
        } else {
          ctx.fillStyle = 'rgba(249, 115, 22, 0.7)'; // orange
          ctx.strokeStyle = '#ea580c';
        }
        ctx.lineWidth = 1.5 / scale;

        ctx.beginPath();
        ctx.arc(cx, cz, debugRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Label: "S:door" or "R:win" + short ID suffix
        const shortType =
          type === 'door' ? 'door' : type === 'window' ? 'win' : 'opn';
        const prefix = isStructure ? 'S' : 'R';
        const shortId = (el.id || '').slice(-6);
        const label = `${prefix}:${shortType} ${shortId}`;

        ctx.fillStyle = isStructure ? '#1e40af' : '#9a3412';
        ctx.font = `${fontSize}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(label, cx, cz + labelOffset);

        // If room copy, also show structureOpeningId link
        if (!isStructure && el.structureOpeningId) {
          const linkId = el.structureOpeningId.slice(-6);
          ctx.fillStyle = '#6b7280';
          ctx.fillText(`→${linkId}`, cx, cz + labelOffset + fontSize * 1.2);
        }

        ctx.restore();
      };

      // Draw structure-level copies (blue)
      (dataToRender.doors || []).forEach((d) =>
        drawCopyMarker(d, 'door', true)
      );
      (dataToRender.windows || []).forEach((w) =>
        drawCopyMarker(w, 'window', true)
      );
      (dataToRender.openings || []).forEach((o) =>
        drawCopyMarker(o, 'opening', true)
      );

      // Draw room-level copies (orange)
      roomDoorCopies.forEach((d) => drawCopyMarker(d, 'door', false));
      roomWindowCopies.forEach((w) => drawCopyMarker(w, 'window', false));
      roomOpeningCopies.forEach((o) => drawCopyMarker(o, 'opening', false));
    }

    // Draw highlighted elements (from room info panel clicks)
    if (highlightedElements.type && highlightedElements.elements?.length > 0) {
      ctx.save();

      // Set highlight style - use a blue glow effect
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#3b82f6';
      ctx.shadowBlur = 10;

      const highlightedItems = highlightedElements.elements;

      switch (highlightedElements.type) {
        case 'walls':
          // Draw highlighted walls - pass each wall as selected for consistent highlighting
          highlightedItems.forEach((wall) => {
            drawWalls(
              ctx,
              [wall],
              scale,
              wall, // Pass the wall as selected item for highlight styling
              debugMode,
              dataToRender.rooms || [],
              resolveForRender(windowsForWalls),
              resolveForRender(doorsForWalls),
              resolveForRender(openingsForWalls),
              {}, // wallExtensionMetadata
              showRoomWallsBold,
              structureWalls, // Pass structure walls
              showDemoedWalls // Pass demoed-wall visibility flag
            );
          });
          break;

        case 'doors':
          // Draw highlighted doors - pass each door as selected for consistent highlighting
          resolveForRender(highlightedItems).forEach((door) => {
            drawDoorsWithRotation(
              ctx,
              [door],
              scale,
              door, // Pass the door as selected item for highlight styling
              debugMode,
              wallsToRender || [],
              showRoomWallsBold,
              structureWalls
            );
          });
          break;

        case 'windows':
          // Draw highlighted windows - pass each window as selected for consistent highlighting
          resolveForRender(highlightedItems).forEach((window) => {
            drawWindows(
              ctx,
              [window],
              scale,
              window, // Pass the window as selected item for highlight styling
              debugMode,
              wallsToRender || [],
              doorsForWalls || [],
              showRoomWallsBold
            );
          });
          break;

        case 'openings':
          // Draw highlighted openings - pass each opening as selected for consistent highlighting
          resolveForRender(highlightedItems).forEach((opening) => {
            drawOpenings(
              ctx,
              [opening],
              scale,
              opening, // Pass the opening as selected item for highlight styling
              debugMode,
              wallsToRender || [],
              showRoomWallsBold,
              structureWalls,
              showDemoedWalls
            );
          });
          break;

        case 'objects':
          // Draw highlighted objects using drawObject for proper rendering
          highlightedItems.forEach((obj) => {
            if (!obj.center) return;

            // Get object type for proper rendering
            const objectType = getObjectType(obj);

            // Draw the object with highlight styling
            drawObject(ctx, obj, {
              isSelected: true, // This will apply highlight styling
              objectType: objectType,
              scale: scale,
              debugMode: false, // Don't show debug lines
              electricalMode, // a highlighted light keeps its symbol
            });
          });
          break;
      }

      ctx.restore();
    }

    // Second pass: Draw selected items on top
    // Draw selected wall with highlighting if not currently dragging
    if (selectedWall && !isDraggingWall) {
      // Draw the selected wall with highlighting
      drawWalls(
        ctx,
        [selectedWall], // Draw the selected wall
        scale,
        selectedWall, // Pass wall for proper highlighting
        debugMode,
        dataToRender.rooms || [],
        resolveForRender(windowsForWalls), // hydrate slim openings before drawWalls
        resolveForRender(doorsForWalls),
        resolveForRender(openingsForWalls),
        {}, // wallExtensionMetadata
        showRoomWallsBold,
        structureWalls, // Pass structure walls
        showDemoedWalls // Pass demoed-wall visibility flag
      );
    }

    if (selectedDoor) {
      // Use selected door directly (it will have updated position during dragging)
      const doorToDisplay = selectedDoor;

      // First, redraw walls that contain this door to show the highlighted opening
      if (wallsToRender.length > 0) {
        // Find walls that might contain this door. The door's
        // parentWallIds[0] points at a STRUCTURE wall id (canonical
        // openings live keyed off structure walls), but wallsToRender
        // are ROOM walls. Match BOTH wall.id (legacy) AND
        // wall.alignment?.structureWallId so room walls referencing the
        // same structure wall get redrawn — without this, the wall-gap
        // pink-fill highlight inside drawWalls never fires.
        const wallsToRedraw = wallsToRender.filter((wall) => {
          const parentId = doorToDisplay.parentWallIds?.[0];
          if (parentId && wall.id) {
            if (parentId === wall.id) return true;
            if (parentId === wall.alignment?.structureWallId) return true;
            // fall through to geometric check
          }

          // Fallback to geometric check if parent ID didn't match
          if (!wall.center || !doorToDisplay.center) return false;
          const distance = Math.sqrt(
            Math.pow(wall.center.x - doorToDisplay.center.x, 2) +
              Math.pow(wall.center.z - doorToDisplay.center.z, 2)
          );
          return distance < 1.0;
        });

        if (wallsToRedraw.length > 0) {
          drawWalls(
            ctx,
            wallsToRedraw,
            scale,
            selectedItem, // Pass selectedItem so wall knows door is selected
            debugMode,
            dataToRender.rooms || [],
            windowsForWalls, // Use collected windows
            doorsForWalls, // Use collected doors
            openingsForWalls, // Use collected openings
            {}, // wallExtensionMetadata
            showRoomWallsBold,
            structureWalls, // Pass structure walls
            showDemoedWalls // Pass demoed-wall visibility flag
          );
          // Same end-cap repaint as the selectedWindow / selectedOpening
          // branches: the wall redraw's destination-out cleared the
          // bold-mode frame rectangles drawDoorsWithRotation painted at
          // each end of the door (the small bars where the door meets
          // the wall). Re-render the selected door so its pink frame
          // end-caps sit on top of the new pink gap fill. The door body
          // + swing arc extend outside the gap so they survive the
          // wipe; only the frame end-caps inside the gap need this
          // repaint.
          drawDoorsWithRotation(
            ctx,
            resolveForRender([selectedDoor]),
            scale,
            selectedItem,
            debugMode,
            wallsToRender || [],
            showRoomWallsBold,
            structureWalls
          );
        }
      }

      // Drag preview: the door body + arc now follow the mouse natively
      // (doorsForWalls has the live `selectedItem` swapped in upstream),
      // so the separate brown semi-transparent overlay this block used to
      // draw is redundant. Removed.

      // Draw green circle at door center (only in debug mode)
      if (debugMode && doorToDisplay.center) {
        ctx.save();
        ctx.fillStyle = '#10b981'; // Green color
        ctx.strokeStyle = '#059669'; // Darker green for border
        ctx.lineWidth = 1.5 / scale;

        const radius = 0.08; // 8cm radius in world space
        ctx.beginPath();
        ctx.arc(
          doorToDisplay.center.x,
          doorToDisplay.center.z,
          radius,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }

    if (selectedWindow) {
      // Use selected window directly (it will have updated position during dragging)
      const windowToDisplay = selectedWindow;

      // First, redraw walls that contain this window to show the highlighted opening
      if (wallsToRender.length > 0) {
        // Same FK-aware parent-wall match as the door block above —
        // window.parentWallIds[0] is a structure wall id, wallsToRender
        // are room walls; check both wall.id AND alignment.structureWallId.
        const wallsToRedraw = wallsToRender.filter((wall) => {
          const parentId = windowToDisplay.parentWallIds?.[0];
          if (parentId && wall.id) {
            if (parentId === wall.id) return true;
            if (parentId === wall.alignment?.structureWallId) return true;
          }

          if (!wall.center || !windowToDisplay.center) return false;
          const distance = Math.sqrt(
            Math.pow(wall.center.x - windowToDisplay.center.x, 2) +
              Math.pow(wall.center.z - windowToDisplay.center.z, 2)
          );
          return distance < 1.0;
        });

        if (wallsToRedraw.length > 0) {
          drawWalls(
            ctx,
            wallsToRedraw,
            scale,
            selectedItem, // Pass selectedItem so wall knows window is selected
            debugMode,
            dataToRender.rooms || [],
            windowsForWalls, // Use collected windows
            doorsForWalls, // Use collected doors
            openingsForWalls, // Use collected openings
            {}, // wallExtensionMetadata
            showRoomWallsBold,
            structureWalls, // Pass structure walls
            showDemoedWalls // Pass demoed-wall visibility flag
          );
          // The wall-redraw above clips + destination-out's the gap
          // rectangle, wiping the pane lines and the pink frame end-caps
          // that drawWindows painted in the first pass. Re-render the
          // selected window so those details land on top of the new
          // pink fill — otherwise the user sees a featureless pink slab
          // with no end-cap markers (the original report: "end caps
          // don't highlight, just the horizontal lines").
          drawWindows(
            ctx,
            resolveForRender([selectedWindow]),
            scale,
            selectedItem,
            debugMode,
            wallsToRender,
            doorsForWalls,
            showRoomWallsBold,
            structureWalls
          );
        }
      }

      // Drag preview: the window body now follows the mouse natively
      // (windowsForWalls has the live `selectedItem` swapped in upstream),
      // so the separate blue semi-transparent overlay this block used to
      // draw is redundant. Removed.

      // Draw green circle at window center (only in debug mode)
      if (debugMode && windowToDisplay.center) {
        ctx.save();
        ctx.fillStyle = '#10b981'; // Green color
        ctx.strokeStyle = '#059669'; // Darker green for border
        ctx.lineWidth = 1.5 / scale;

        const radius = 0.08; // 8cm radius in world space
        ctx.beginPath();
        ctx.arc(
          windowToDisplay.center.x,
          windowToDisplay.center.z,
          radius,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }

    if (selectedOpening) {
      // Use selected opening directly (it will have updated position during dragging)
      const openingToDisplay = selectedOpening;

      // First, redraw walls that contain this opening to show the highlighted opening
      if (wallsToRender.length > 0) {
        // Same FK-aware parent-wall match as the door / window blocks
        // above — opening.parentWallIds[0] is a structure wall id,
        // wallsToRender are room walls; check both wall.id AND
        // alignment.structureWallId. Cased openings have no separate
        // body render, so this redraw is the ONLY pink highlight path
        // for them — getting it wrong = no highlight at all on click.
        const wallsToRedraw = wallsToRender.filter((wall) => {
          const parentId = openingToDisplay.parentWallIds?.[0];
          if (parentId && wall.id) {
            if (parentId === wall.id) return true;
            if (parentId === wall.alignment?.structureWallId) return true;
          }

          if (!wall.center || !openingToDisplay.center) return false;
          const distance = Math.sqrt(
            Math.pow(wall.center.x - openingToDisplay.center.x, 2) +
              Math.pow(wall.center.z - openingToDisplay.center.z, 2)
          );
          return distance < 1.0;
        });

        if (wallsToRedraw.length > 0) {
          drawWalls(
            ctx,
            wallsToRedraw,
            scale,
            selectedItem, // Pass selectedItem so wall knows opening is selected
            debugMode,
            dataToRender.rooms || [],
            windowsForWalls, // Use collected windows
            doorsForWalls, // Use collected doors
            openingsForWalls, // Use collected openings
            {}, // wallExtensionMetadata
            showRoomWallsBold,
            structureWalls, // Pass structure walls
            showDemoedWalls // Pass demoed-wall visibility flag
          );
          // Same end-cap repaint as the selectedWindow branch: the wall
          // redraw's destination-out cleared the bold-mode frame
          // rectangles drawOpenings painted in the first pass.
          // Re-render the selected opening so those pink frame end-caps
          // sit on top of the new pink gap fill.
          if (showRoomWallsBold) {
            drawOpenings(
              ctx,
              resolveForRender([selectedOpening]),
              scale,
              selectedItem,
              debugMode,
              wallsToRender || [],
              showRoomWallsBold,
              structureWalls,
              showDemoedWalls
            );
          }
        } else {
          console.warn(
            '[DEBUG] No walls found to redraw for opening highlight'
          );
        }
      }

      // Note: drawOpenings runs once unconditionally above (line ~7198)
      // for all openings, and again here just for the selected opening
      // so the wall-redraw doesn't leave the selected opening's
      // bold-mode frame rectangles wiped.

      // Drag preview: the opening body / fill now follows the mouse
      // natively (openingsForWalls has the live `selectedItem` swapped in
      // upstream), so the separate pink semi-transparent overlay this
      // block used to draw is redundant. Removed.

      // Draw green circle at opening center (only in debug mode)
      if (debugMode && openingToDisplay.center) {
        ctx.save();
        ctx.fillStyle = '#10b981'; // Green color
        ctx.strokeStyle = '#059669'; // Darker green for border
        ctx.lineWidth = 1.5 / scale;

        const radius = 0.08; // 8cm radius in world space
        ctx.beginPath();
        ctx.arc(
          openingToDisplay.center.x,
          openingToDisplay.center.z,
          radius,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }

    if (selectedObject && showObjects) {
      // The selected fixture is rendered in the single first pass above;
      // this block now exists only for selection-driven overlays
      // (clearance zones during drag, etc.).

      // Draw clearance zones when selected in move mode or during drag
      if (
        selectedItem &&
        !isWall(selectedItem) &&
        (isDraggingObject ||
          objectInteractionMode === 'move' ||
          objectInteractionMode === 'move-group')
      ) {
        const dragRoom =
          selectedRoom ||
          (selectedItem._roomId && structureData?.rooms
            ? structureData.rooms.find((r) => r.id === selectedItem._roomId)
            : null);
        if (dragRoom) {
          // Under a live run preview the ghost has already moved, so the
          // zones must be computed from the PREVIEWED objects — the subject
          // and its neighbours alike. Drawn from the room's stored copies, an
          // appliance's clearance sat where the appliance used to be, and the
          // repacked cabinets' zones with it, for the whole of a bay drag.
          const { subject: clearanceSubject, objects: clearanceObjects } =
            overlayObjectsUnderPreview(
              selectedItem,
              dragRoom.objects,
              appliancePreview
            );
          drawDragClearanceZones(
            ctx,
            clearanceSubject,
            {
              objects: clearanceObjects,
              // Doors need structure-canonical coords + doorType +
              // wallFacesPriorityRoom for the swing-arc / collision math —
              // post-P3a those live only on the structure copy. Resolve
              // each iterated room copy here before clearanceDrawing reads
              // off it.
              doors: resolveForRender(dragRoom.doors),
              walls: dragRoom.walls || [],
            },
            scale
          );
        }
      }

      // Draw resize handles on selected object (only in resize mode).
      // canvasResizeHandlesFor restricts the set for wall-mounted
      // openings + demoed walls to the two end handles (see helper at
      // top of file).
      // A RUN-OWNED object is excluded: on it, resize mode means the RUN's
      // end handles (`runDrag`), which are drawn by the run overlay. Drawing
      // the object's own handles as well put a second set of grips around the
      // cabinet, as if the box itself resized — it does not; the run re-solves
      // and the box is refilled from the spec.
      if (
        selectedItem &&
        !selectedItem.isRoomLabel &&
        !selectedItem.isWall &&
        !runDrag?.runIdFor?.(selectedItem) &&
        objectInteractionMode === 'resize'
      ) {
        const objectToDrawHandles = tempResizedObject || selectedItem;
        drawResizeHandles(
          ctx,
          objectToDrawHandles,
          scale,
          activeResizeHandle,
          isResizing || isDraggingObject, // Show preview when resizing or dragging
          canvasResizeHandlesFor(selectedItem),
          { coarse: grabHandlesEnabled }
        );
      }
    }

    // Touch affordances for Move. The grab handle is a finger-sized target
    // beside the selected element while Move is armed (only where a finger
    // is or has been — see useCoarsePointer); it disappears during the
    // drag. A wall-mounted element cannot ride above the finger, so while
    // a FINGER drags one the readout above it says how much wall is left
    // on either side.
    // A run-owned object gets the handle only when it is a bay the run
    // lets drag ("Move along run"): the bay drag projects the pointer onto
    // the wall, so a handle offset ACROSS the wall does not move the
    // appliance along it. Through `grabHandleOfferedFor`, which the touch
    // hit-test asks too — the two must never disagree about whether a
    // handle is there.
    // A bay that slides along its run gets the handle for EVERY pointer and
    // in any mode — it is the MOVE button (user request 2026-10-26), drawn
    // with a two-way arrow along the bay because that is its one axis. The
    // press is `useFloorplanGestures`', through the same predicate.
    const offersMoveHandle =
      !!selectedItem &&
      !isWall(selectedItem) &&
      moveHandleOfferedFor(selectedItem, runDrag, cabinetEditScope);
    if (
      selectedItem &&
      !selectedItem.isRoomLabel &&
      !isWall(selectedItem) &&
      (offersMoveHandle ||
        (grabHandlesEnabled &&
          grabHandleOfferedFor(selectedItem, runDrag) &&
          (objectInteractionMode === 'move' ||
            objectInteractionMode === 'move-group'))) &&
      !isDraggingObject &&
      !isDraggingRun &&
      !isDraggingWallElement
    ) {
      const handleRoom =
        selectedRoom ||
        (selectedItem._roomId && structureData?.rooms
          ? structureData.rooms.find((r) => r.id === selectedItem._roomId)
          : null);
      const along = offersMoveHandle
        ? apply3DRotationTo2D(selectedItem.rotation)
        : null;
      drawGrabHandle(
        ctx,
        tempResizedObject || selectedItem,
        scale,
        roomCentroid(handleRoom),
        along ? { glyph: { x: along[0], z: along[1] } } : undefined
      );
    }
    // Same idea for a finger RESIZE: the handle must stay under the
    // finger (the edge is what is being placed), so the size is read out
    // above the object instead — width for an opening, width × depth for
    // a fixture.
    if (
      dragPointer === 'touch' &&
      (isResizing || (isDraggingRun && runDrag?.active?.kind === 'end')) &&
      selectedItem &&
      !isWall(selectedItem)
    ) {
      const sized = tempResizedObject || selectedItem;
      const dims = sized?.dimensions;
      if (dims?.x) {
        const isOpening =
          selectedItem.category?.door ||
          selectedItem.category?.window ||
          selectedItem.category?.opening;
        const text = isOpening
          ? formatFeetInches(dims.x)
          : `${formatFeetInches(dims.x)}  ×  ${formatFeetInches(dims.z)}`;
        drawReadoutPill(ctx, sized.center, text, scale);
      }
    }
    if (dragPointer === 'touch' && isDraggingWallElement && selectedItem) {
      const wall = findParentWallInStructure(selectedItem, {
        walls: structureData?.walls,
        structureWalls,
        rooms: structureData?.rooms,
      });
      const span = wallSpanFromEnds(selectedItem, wall);
      if (span) {
        drawWallSpanReadout(ctx, selectedItem, span, scale, formatFeetInches);
      }
    }

    // Draw collision debug overlay (always visible in debug mode)
    if (debugMode) {
      // Resolve disabled constraints from the active room (selected item's room or selected room)
      const activeRoomId = selectedItem?._roomId || selectedRoom?.id;
      const activeRoom =
        activeRoomId && structureData?.rooms
          ? structureData.rooms.find((r) => r.id === activeRoomId)
          : null;
      const activeDisabledConstraints =
        activeRoom?.disabledConstraints || disabledConstraints;

      drawCollisionDebugOverlay(ctx, collisionDebugRef.current, scale, {
        collapsed: debugPanelCollapsed,
        disabledConstraints: activeDisabledConstraints,
      });
    }

    // A free-standing leg's DATUM, whenever one of its objects is selected —
    // in every interaction mode, not just resize. The island panel measures
    // `depth`, `frontDepth` and the knee from this line, and it sits where
    // nobody expects (the front row's back face, not the centreline), so the
    // numbers in the panel are unreadable while it is invisible.
    // Gated on `runId`, not on the `freeSegIndex` stamp: `freeSegmentDatum`
    // resolves the leg from the RUN and returns null for anything that is not
    // one, so an island derived but never re-solved still gets its datum.
    if (selectedItem && !isWall(selectedItem) && selectedItem.runId) {
      const legRoom =
        selectedRoom ||
        (selectedItem._roomId && structureData?.rooms
          ? structureData.rooms.find((r) => r.id === selectedItem._roomId)
          : null) ||
        (structureData?.rooms || []).find((r) =>
          r.objects?.some((o) => o?.id === selectedItem.id)
        );
      // Mid-drag the datum follows the PREVIEWED leg, exactly as the run-end
      // handles below follow the previewed run: the commit is deferred to
      // mouseup, so the stored leg is still where the drag started and the
      // disc would sit behind the island's ghost until the user let go.
      const datum = freeSegmentDatum(
        legRoom,
        selectedItem,
        isDraggingRun && runDrag?.active?.kind === 'leg'
          ? appliancePreview?.stored
          : null
      );
      if (datum) {
        drawFreeSegmentDatum(ctx, datum, scale);
        // The grab point for the island, drawn while the RUN is the edit
        // target. No mode in front of it — grabbing a handle is unambiguous,
        // and only a drag on an object's BODY needs one — but it follows the
        // SCOPE, because the pink already says which of the bay and the run
        // the next edit changes and the handles must not contradict it.
        if (cabinetEditScope === 'run' && runDrag?.canDragLeg?.(selectedItem)) {
          drawFreeSegmentMoveHandle(ctx, datum, scale, {
            coarse: grabHandlesEnabled,
            active: isDraggingRun && runDrag.active?.kind === 'leg',
          });
        }
      }
    }

    // A cabinet run's END handles, drawn while the RUN is the edit target —
    // the scope the whole run is painted pink in. No mode in front of them
    // (the handle IS the affordance); `useFloorplanGestures` and
    // `runPointerTargets` read the same scope, so draw and hit-test cannot
    // disagree about whether a handle is there.
    if (
      selectedItem &&
      !isWall(selectedItem) &&
      cabinetEditScope === 'run' &&
      runDrag?.runIdFor?.(selectedItem)
    ) {
      // Mid-drag the handles follow the PREVIEWED run, so the one being
      // dragged rides the cursor even where the fill places no cabinet
      // (a stretch under a window shows nothing until the wall's end);
      // the stored run's handle would sit at the old end and read as
      // the drag having died.
      const handles =
        isDraggingRun && appliancePreview?.run
          ? runEndHandles(appliancePreview.run)
          : runDrag.handlesFor(selectedItem);
      drawRunEndHandles(
        ctx,
        handles,
        scale,
        isDraggingRun ? runDrag.active?.end : null,
        { coarse: grabHandlesEnabled }
      );
      // …and its CORNERS, where two sections meet: drag one to make a
      // section longer or shorter (`runCornerMove`). The same disc, with the
      // four-way glyph — a corner between two island sections moves along
      // either — and greyed where it is held. Mid-drag they ride the ghost.
      const corners =
        isDraggingRun && appliancePreview?.run
          ? cornerHandlesOfSegments(appliancePreview.run.segments)
          : runDrag.cornerHandlesFor?.(selectedItem) || [];
      drawRunEndHandles(
        ctx,
        corners.map((c) => ({
          end: `corner-${c.cornerIndex}`,
          point: c.point,
          dir: null,
          pinned: c.pinned,
        })),
        scale,
        isDraggingRun && runDrag.active?.kind === 'corner'
          ? `corner-${runDrag.active.cornerIndex}`
          : null,
        { coarse: grabHandlesEnabled }
      );
    }

    // The selected BAY's own two edges — the other scope, and never on screen
    // at the same time as the run's. Mid-drag they follow the PREVIEWED run for
    // the same reason the end handles do: the commit is deferred to mouseup, so
    // the stored bay is still its old width and the handle would sit behind the
    // ghost until the user let go.
    if (
      selectedItem &&
      !isWall(selectedItem) &&
      cabinetEditScope === 'bay' &&
      runDrag?.runIdFor?.(selectedItem)
    ) {
      const edgeObject =
        isDraggingRun && runDrag.active?.kind === 'bay-width'
          ? (appliancePreview?.objects || []).find(
              (o) => o.overrideId === bayOverrideIdOf(selectedItem)
            ) || selectedItem
          : selectedItem;
      drawBayEdgeHandles(
        ctx,
        runDrag.bayEdgeHandlesFor?.(edgeObject) || [],
        scale,
        isDraggingRun && runDrag.active?.kind === 'bay-width'
          ? runDrag.active.side
          : null,
        { coarse: grabHandlesEnabled }
      );
    }

    // Draw wall handles on selected wall (only in resize mode, and only while
    // the end-handle drag is offered — see WALL_ENDPOINT_DRAG_ENABLED).
    if (
      selectedItem &&
      isWall(selectedItem) &&
      objectInteractionMode === 'resize' &&
      WALL_ENDPOINT_DRAG_ENABLED
    ) {
      const wallToEdit = selectedItem;
      drawWallHandles(
        ctx,
        wallToEdit,
        scale,
        activeWallHandle,
        isDraggingWall,
        debugMode,
        { coarse: grabHandlesEnabled }
      );

      // Draw red circle at wall center for debugging rotation (only in debug mode)
      if (debugMode) {
        ctx.save();
        ctx.fillStyle = 'red';
        ctx.strokeStyle = 'darkred';
        ctx.lineWidth = 2 / scale;

        // Draw circle at the wall center
        const centerX = wallToEdit.center.x;
        const centerZ = wallToEdit.center.z;
        const radius = 0.15; // 15cm radius in world space

        ctx.beginPath();
        ctx.arc(centerX, centerZ, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // If dragging, also show the original center with a hollow circle
        if (isDraggingWall && originalWallState) {
          ctx.fillStyle = 'transparent';
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 2 / scale;
          ctx.setLineDash([5 / scale, 5 / scale]);

          ctx.beginPath();
          ctx.arc(
            originalWallState.center.x,
            originalWallState.center.z,
            radius,
            0,
            Math.PI * 2
          );
          ctx.stroke();

          // Draw a line connecting original to current center
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(originalWallState.center.x, originalWallState.center.z);
          ctx.lineTo(centerX, centerZ);
          ctx.stroke();
        }

        ctx.restore();
      }
    } else if (
      selectedItem &&
      selectedItem.category &&
      !selectedItem.category.door &&
      !selectedItem.category.window &&
      !selectedItem.category.opening &&
      !selectedItem.isWall &&
      !selectedItem.isRoomLabel
    ) {
      //   selectedItem,
      //   showObjects,
      //   selectedObject,
      // });
    }

    // Take-off "Show" overlay: highlights what each room-info-panel
    // measurement is computing.
    if (takeOffOverlay && takeOffOverlay.field && takeOffOverlay.room) {
      drawTakeOffOverlay(ctx, takeOffOverlay, scale);
    }
  };

  // === BEGIN VERBATIM (UVFloorPlanCanvas.js L1329-L2270) ===
  useEffect(() => {
    if (!structureData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;

    // Clear and prepare canvas (applies DPR scaling)
    clearCanvas(ctx, width, height);

    // Set up canvas transformation
    setupCanvasTransform(ctx, width, height, pan, scale, rotation);

    // Get data to render (filtered by selected room if applicable)
    const dataToRender = getDataToRender(structureData);

    // Draw the floor plan in local coordinates
    drawFloorPlan(ctx, dataToRender, structureData, debugMode); // Pass both filtered and original structureData

    // Draw preview object if in placement mode
    if (previewObject) {
      ctx.save();

      // If it needs a wall but isn't snapped, show warning indicator
      if (previewObject.needsWall && !previewObject.isSnappedToWall) {
        ctx.globalAlpha = 0.3; // Very transparent to indicate invalid placement

        // Draw a red X or warning indicator
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 2 / scale;
        const indicatorSize = 0.2;
        ctx.beginPath();
        ctx.moveTo(
          previewObject.center.x - indicatorSize,
          previewObject.center.z - indicatorSize
        );
        ctx.lineTo(
          previewObject.center.x + indicatorSize,
          previewObject.center.z + indicatorSize
        );
        ctx.moveTo(
          previewObject.center.x + indicatorSize,
          previewObject.center.z - indicatorSize
        );
        ctx.lineTo(
          previewObject.center.x - indicatorSize,
          previewObject.center.z + indicatorSize
        );
        ctx.stroke();
      } else {
        ctx.globalAlpha = 0.7; // Normal preview transparency
      }

      // Apply rotation transformation
      ctx.translate(previewObject.center.x, previewObject.center.z);

      // Debug logging for wall light rotation
      if (previewObject.type === 'wall-light') {
        console.log('🎨 Drawing wall light preview:');
        console.log(
          '  Rotation from snapResult (radians):',
          previewObject.rotation?.y || 0
        );
        console.log(
          '  Rotation from snapResult (degrees):',
          ((previewObject.rotation?.y || 0) * 180) / Math.PI
        );
        console.log('  isSnappedToWall:', previewObject.isSnappedToWall);
      }

      // Use the same rotation calculation as the actual drawing code in elementDrawingUtils.js
      // Walls, doors, windows, openings use default [1, 0, 0] initial direction
      // Other objects (fixtures, furniture) use [0, 0, 1] initial direction
      const isWallLikeElement =
        previewObject.category?.door ||
        previewObject.category?.window ||
        previewObject.category?.opening;

      const normalizedDir = isWallLikeElement
        ? apply3DRotationTo2D(previewObject.rotation) // Default [1, 0, 0] for wall-like elements
        : apply3DRotationTo2D(previewObject.rotation, [0, 0, 1]); // [0, 0, 1] for objects
      const objectAngle = Math.atan2(normalizedDir[1], normalizedDir[0]);

      if (window.DEBUG_PREVIEW_PLACEMENT) {
        console.log('  Applied objectAngle (radians):', objectAngle);
        console.log(
          '  Applied objectAngle (degrees):',
          (objectAngle * 180) / Math.PI
        );
      }

      ctx.rotate(objectAngle);

      const width = previewObject.dimensions.x;
      const depth = previewObject.dimensions.z;
      const x = -width / 2;
      const z = -depth / 2;

      // Draw based on type
      if (previewObject.category?.door) {
        // Draw door schematic with pink highlight colors
        const doorThickness = 0.05; // 5cm thickness

        // Draw door frame
        ctx.strokeStyle = previewObject.isSnappedToWall
          ? HIGHLIGHT_COLORS.PRIMARY
          : '#FF6666';
        ctx.lineWidth = 2 / scale;
        ctx.strokeRect(x, -doorThickness / 2, width, doorThickness);

        // Draw door swing arc
        ctx.beginPath();
        ctx.arc(x, 0, width, 0, Math.PI / 2, false);
        ctx.stroke();

        // Draw door panel at the end of the arc
        // Arc ends at PI/2 (90 degrees), which is downward
        ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY_DARK;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(
          x + width * Math.cos(Math.PI / 2),
          width * Math.sin(Math.PI / 2) // Positive because arc goes down
        );
        ctx.stroke();
      } else if (previewObject.category?.window) {
        // Draw window schematic with pink highlight colors
        ctx.strokeStyle = previewObject.isSnappedToWall
          ? HIGHLIGHT_COLORS.PRIMARY
          : '#FF6666';
        ctx.lineWidth = 3 / scale;

        // Draw window frame
        ctx.strokeRect(x, -depth / 2, width, depth);

        // Draw window panes (double lines)
        ctx.lineWidth = 1 / scale;
        const paneOffset = depth / 4;
        ctx.beginPath();
        ctx.moveTo(x, -paneOffset);
        ctx.lineTo(x + width, -paneOffset);
        ctx.moveTo(x, paneOffset);
        ctx.lineTo(x + width, paneOffset);
        ctx.stroke();
      } else if (previewObject.category?.opening) {
        // Draw opening schematic with pink highlight colors
        ctx.strokeStyle = previewObject.isSnappedToWall
          ? HIGHLIGHT_COLORS.PRIMARY
          : '#FF6666';
        ctx.lineWidth = 2 / scale;
        ctx.setLineDash([5 / scale, 5 / scale]);

        // Draw dashed rectangle for opening
        ctx.strokeRect(x, -depth / 2, width, depth);
        ctx.setLineDash([]);
      } else {
        // Regular object preview - use drawObjectShape for proper schematic drawing
        ctx.save();

        // No extra rotation needed - the objectAngle calculated above using [0, 0, 1]
        // matches the actual drawObject function in elementDrawingUtils.js

        // Use pink highlight color for all preview objects (same as selected items)
        ctx.fillStyle = HIGHLIGHT_COLORS.PRIMARY_FILL; // Light pink/purple fill
        ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY; // Pink/purple outline
        ctx.lineWidth = 2 / scale;

        // Use drawObjectShape to render the actual schematic
        const height = previewObject.dimensions.y || 1.0; // Default height if not specified

        // Draw with original dimensions - no extra rotation needed.
        // `previewObject` is passed so a light previews as the same electrical
        // symbol it will be drawn with once placed.
        drawObjectShape(
          ctx,
          previewObject.type,
          width,
          height,
          depth,
          scale,
          0,
          {},
          previewObject,
          { electricalMode }
        );

        ctx.restore();

        // For objects without distinctive shapes, show label
        const hasDistinctiveShape = [
          'toilet',
          'sink',
          'bathtub',
          'tub-shower',
          'shower',
          'vanity',
          // Both old (stove / oven) and new (range / wall-oven) names.
          'stove',
          'range',
          'oven',
          'wall-oven',
          'fridge',
          'refrigerator',
          'dishwasher',
          'cabinet',
          'dresser',
          'fireplace',
          'stairs',
          'sofa',
          'bed',
          'chair',
          'table',
          'washerDryer',
          'storage',
          'shelf',
          'bookshelf',
          'television',
          'shower-door',
        ].includes(previewObject.type);

        if (!hasDistinctiveShape) {
          // Draw label for objects without distinctive shapes
          ctx.fillStyle = '#FFFFFF';
          ctx.font = `${14 / scale}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(previewObject.label || previewObject.type, 0, 0);
        }
      }

      ctx.restore();
    }

    // Draw room polygon preview when in room drawing mode
    if (roomDrawing?.isActive) {
      drawRoomPolygonPreview(
        ctx,
        roomDrawing.vertices,
        roomDrawing.cursorPos,
        roomDrawing.snapInfo,
        roomDrawing.isNearFirstVertex,
        scale
      );
      if (roomDrawing.snapInfo) {
        drawSnapIndicator(
          ctx,
          roomDrawing.snapInfo.position,
          roomDrawing.snapInfo.type,
          scale,
          roomDrawing.snapInfo.target
        );
      }
    }

    // Draw cabinet-run preview when in cabinet-run drawing mode
    if (cabinetRunDrawing?.isActive) {
      drawCabinetRunPreview(
        ctx,
        cabinetRunDrawing.pathPoints,
        cabinetRunDrawing.vertices,
        cabinetRunDrawing.cursorPos,
        cabinetRunDrawing.snapInfo,
        scale,
        {
          angleDeg: cabinetRunDrawing.previewAngleDeg,
          tooSharp: cabinetRunDrawing.previewTooSharp,
          view: { width, height, pan, scale, rotation },
        }
      );
    }

    // Recessed-row preview: the line you are drawing plus a ghost can at every
    // position the row will fill. Positions come from `rowCandidates` — the
    // SAME function that plans the committed row — so the preview cannot
    // promise a layout the commit does not produce.
    if (recessedRowDrawing?.isActive && recessedRowDrawing.start) {
      const from = recessedRowDrawing.start;
      const to = recessedRowDrawing.cursorPos || from;
      ctx.save();
      ctx.setLineDash([6 / scale, 4 / scale]);
      ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
      ctx.lineWidth = Math.max(0.01, 1.5 / scale);
      ctx.beginPath();
      ctx.moveTo(from.x, from.z);
      ctx.lineTo(to.x, to.z);
      ctx.stroke();

      ctx.setLineDash([]);
      const ghostR = 9 / scale;
      rowCandidates(
        from,
        to,
        recessedRowDrawing.spacing,
        recessedRowDrawing.align
      ).forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.z, ghostR, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(p.x, p.z, ghostR * 0.5, 0, Math.PI * 2);
        ctx.stroke();
      });
      ctx.restore();
    }

    // Wall-chain preview: the stretches tapped so far plus the one being
    // drawn, each as a band the wall's thickness, and the drawn stretch's
    // length (and angle, when it is not square) in the readout pill.
    if (wallChainDrawing?.isActive && wallChainDrawing.taps?.length) {
      const pts = [...wallChainDrawing.taps];
      const cursor = wallChainDrawing.cursorPos;
      if (cursor) pts.push(cursor);
      ctx.save();
      ctx.lineJoin = 'miter';
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].z);
      for (const p of pts.slice(1)) ctx.lineTo(p.x, p.z);
      ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY_FILL;
      ctx.lineWidth = WALL_THICKNESS;
      ctx.stroke();
      ctx.strokeStyle = HIGHLIGHT_COLORS.PRIMARY;
      ctx.lineWidth = Math.max(0.005, 1.5 / scale);
      ctx.stroke();
      ctx.fillStyle = HIGHLIGHT_COLORS.PRIMARY;
      for (const p of wallChainDrawing.taps) {
        ctx.beginPath();
        ctx.arc(p.x, p.z, 3 / scale, 0, Math.PI * 2);
        ctx.fill();
      }
      if (
        cursor &&
        wallChainDrawing.snap &&
        wallChainDrawing.snap.type !== 'free'
      ) {
        ctx.beginPath();
        ctx.arc(cursor.x, cursor.z, 6 / scale, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      const last = wallChainDrawing.taps[wallChainDrawing.taps.length - 1];
      if (cursor) {
        drawReadoutPill(
          ctx,
          { x: (last.x + cursor.x) / 2, z: (last.z + cursor.z) / 2 },
          chainReadoutText(wallChainDrawing.readout),
          scale
        );
      }
    }

    // Draw vertex delete overlay when in vertex delete mode
    if (vertexDelete?.isActive && selectedRoom) {
      const roomVertices = selectedRoom.floors?.[0]?.polygonCorners;
      if (roomVertices && roomVertices.length >= 3) {
        drawVertexDeleteOverlay(
          ctx,
          roomVertices,
          vertexDelete.hoveredVertexIndex,
          scale
        );
      }
    }

    // Draw vertex add overlay when in vertex add mode
    if (vertexAdd?.isActive && selectedRoom) {
      const roomVertices = selectedRoom.floors?.[0]?.polygonCorners;
      if (roomVertices && roomVertices.length >= 3) {
        drawVertexAddOverlay(
          ctx,
          roomVertices,
          vertexAdd.hoveredEdgeIndex,
          vertexAdd.previewPoint,
          scale,
          vertexAdd.insertedVertexIndex,
          vertexAdd.previewPosition,
          vertexAdd.snapInfo
        );
      }
    }

    // Draw vertex move overlay when in vertex move mode
    if (vertexMove?.isActive && selectedRoom) {
      const roomVertices = selectedRoom.floors?.[0]?.polygonCorners;
      if (roomVertices && roomVertices.length >= 3) {
        drawVertexMoveOverlay(
          ctx,
          roomVertices,
          vertexMove.hoveredVertexIndex,
          vertexMove.selectedVertexIndex,
          vertexMove.previewPosition,
          scale,
          vertexMove.snapInfo
        );
      }
    }

    // Draw measurements using new data format
    if (shouldShowMeasurements) {
      let allMeasurements = [];

      if (selectedRoom) {
        // Room selected — show fixture measurements for that room.
        // Hydrate the slim room copies (which carry only id +
        // structureOpeningId + parentWallIds + category post-PR #634) with
        // the canonical structure-side geometry. Two reasons:
        //   1. The slim copies have no center / dimensions / rotation, so
        //      `calculateFixtureMeasurements` → `getObjectMeasurementPoints`
        //      would bail on every opening — that's the regression we're
        //      fixing here.
        //   2. We can't just substitute the canonical copies wholesale:
        //      their `parentWallIds` point at STRUCTURE wall ids, but
        //      `calculateRoomMeasurementsV2` builds `wallSegments` from
        //      `room.walls` and matches openings to walls via
        //      `parentWallIds`. Structure wall ids would never match.
        //      `resolveRoomOpening` returns canonical geometry *with the
        //      slim copy's room-wall `parentWallIds` preserved* — exactly
        //      what the per-wall match needs.
        const roomObjects = selectedRoom.objects || [];
        const canonDoorById = new Map(
          (structureData?.doors || []).map((d) => [d.id, d])
        );
        const canonWindowById = new Map(
          (structureData?.windows || []).map((w) => [w.id, w])
        );
        const roomDoors = (selectedRoom.doors || []).map((slim) =>
          resolveRoomOpening(
            slim,
            canonDoorById.get(slim.structureOpeningId || slim.id)
          )
        );
        const roomWindows = (selectedRoom.windows || []).map((slim) =>
          resolveRoomOpening(
            slim,
            canonWindowById.get(slim.structureOpeningId || slim.id)
          )
        );
        // Frameless openings / pass-throughs (category.opening) live in
        // selectedRoom.openings[], a separate array from doors/windows. They
        // were previously never passed to the measurement pipeline, so they
        // got no side-to-wall measurements at all. Hydrate them the same way.
        const canonOpeningById = new Map(
          (structureData?.openings || []).map((o) => [o.id, o])
        );
        // Demoed walls live in openings[] too. Every opening keeps its
        // measurements regardless of demoed state or the showDemoedWalls
        // toggle, so they always flow through the pipeline.
        const roomOpenings = (selectedRoom.openings || []).map((slim) =>
          resolveRoomOpening(
            slim,
            canonOpeningById.get(slim.structureOpeningId || slim.id)
          )
        );

        const filteredObjectsForMeasurements = filterObjectsByHeight(
          roomObjects,
          structureData
        );

        allMeasurements = calculateRoomMeasurementsV2(
          selectedRoom,
          filteredObjectsForMeasurements,
          roomDoors,
          roomWindows,
          measurementMode,
          roomOpenings,
          // A room wall is a plane; its FACES come from the structure wall
          // behind it, which a dimension to "the wall" must measure to.
          structureWalls
        );
      } else {
        // No room selected — wall length measurements for every room of the
        // LOWEST floor (parity with the 3D viewer: stacked stories share X/Z,
        // so upper-floor strings would land on the ground floor's).
        allMeasurements = shownWallLengthMeasurements(structureData, scale);
      }

      // When something is selected, hide measurements that aren't
      // relevant. Filtering measurementsRef too means hit-testing only
      // finds the visible ones.
      //   • Object selected → measurements whose from/to/relatedObjectIds
      //     include any of that object's synonymous ids.
      //   • Wall selected   → only that wall's T0 (wall total) and T0.5
      //     (openings on the wall). Fixture-tier detail is hidden.
      // Synonyms cover the structure-vs-room id split: clicking a window
      // selects the structure copy (id `sw_…`), but measurements were
      // built from the room copy (id `rm_…`) linked back via
      // `structureOpeningId`. We collect both ids for matching.
      const selectedObjectId = selectedItem?.id;
      const selectedIsWall = selectedItem && isWall(selectedItem);
      const selectedIsRoomLabel = !!(selectedItem && selectedItem.isRoomLabel);

      const selectedObjectSynonyms = new Set();
      // Don't treat the room itself as a "selected object" — measurements
      // are between fixtures / walls / openings, never a room id, so the
      // synonyms filter would empty everything out.
      if (selectedObjectId && !selectedIsRoomLabel) {
        selectedObjectSynonyms.add(selectedObjectId);
        if (selectedItem.structureOpeningId) {
          selectedObjectSynonyms.add(selectedItem.structureOpeningId);
        }
        if (structureData?.rooms) {
          structureData.rooms.forEach((room) => {
            ['doors', 'windows', 'openings'].forEach((key) => {
              room[key]?.forEach((o) => {
                if (
                  o?.id &&
                  o.structureOpeningId &&
                  o.structureOpeningId === selectedObjectId
                ) {
                  selectedObjectSynonyms.add(o.id);
                }
              });
            });
          });
        }
      }

      let visibleMeasurements = allMeasurements;
      if (selectedIsWall && selectedObjectId) {
        const acceptedWallIds = new Set([selectedObjectId]);
        if (selectedItem._isStructureWall && structureData?.rooms) {
          structureData.rooms.forEach((room) => {
            room.walls?.forEach((w) => {
              if (
                w?.id &&
                (w.alignment?.structureWallId === selectedObjectId ||
                  w.id === selectedObjectId)
              ) {
                acceptedWallIds.add(w.id);
              }
            });
          });
        }
        visibleMeasurements = allMeasurements.filter(
          (m) =>
            acceptedWallIds.has(m.wallId) && (m.tier === 0 || m.tier === 0.5)
        );
      } else if (selectedObjectSynonyms.size > 0) {
        visibleMeasurements = allMeasurements.filter(
          (m) =>
            measurementInvolvesAnyObject(m, selectedObjectSynonyms) ||
            (Array.isArray(m.relatedObjectIds) &&
              m.relatedObjectIds.some((rid) => selectedObjectSynonyms.has(rid)))
        );
      }

      // In the dimensions layer the STATED ones are the subject, so they show
      // whatever is selected — the selection filters above narrow to the
      // generated chains around one object, which is the opposite of the point.
      if (showDimensionsOnly) {
        visibleMeasurements = onlyStatedDimensions(allMeasurements);
      }

      const measurementLabelPx = measurementFontPx ?? 12;
      visibleMeasurements = layoutMeasurements(visibleMeasurements, {
        measureWidth: (label) => {
          ctx.font = `500 ${measurementLabelPx}px Arial, sans-serif`;
          return ctx.measureText(label).width;
        },
        scale,
        fontPx: measurementLabelPx,
      });
      measurementsRef.current = visibleMeasurements;
      drawMeasurementsV2(
        ctx,
        visibleMeasurements,
        scale,
        shouldShowMeasurements,
        {
          selectedMeasurement: selectedFloorPlanMeasurement,
          selectedObjectIds: selectedObjectSynonyms,
          previewMap,
          lockedKeys,
          labelFontPx: measurementLabelPx,
          // In the dimensions layer the stated ones are the subject, not
          // read-only clutter, so they draw at full contrast.
          emphasiseStated: !!showDimensionsOnly,
        }
      );
    } else {
      measurementsRef.current = [];
    }

    // Electrical overlay: light highlights with the Electrical toggle, wires
    // with the Wiring toggle.
    if (electricalMode || showWiring || connectionMode) {
      // Switch to screen space for the wiring, on the SAME pixels the plan
      // was just drawn on. The hand-rolled version this replaces reset to the
      // identity matrix (dropping the DPR) and ignored `rotation`, so every
      // wire and target ring sat off its fixture — see beginScreenSpaceOverlay.
      const worldToScreen = beginScreenSpaceOverlay(ctx, {
        width,
        height,
        pan,
        scale,
        rotation,
      });

      // Wiring is scoped to what is ON SCREEN, but a wire needs only ONE end
      // there: a run that starts at a switch in the room you are looking at
      // belongs to that room even though it carries on into the next one.
      // So positions resolve against EVERY object (the far end still has a
      // real place to run to) while `visibleIds` — the set `drawFloorPlan`
      // just drew, already narrowed by the room isolation, the storey and the
      // layer toggles — decides which wires are drawn at all.
      const allObjects = collectAllObjects(structureData);
      const visibleIds = new Set(
        (drawnObjectsRef.current || []).map((o) => o?.id)
      );
      const allElectricalData = collectAllElectricalData(structureData);

      // Draw the electrical overlay (switches, lights, wiring)
      const runs = buildWiringRuns(structureData?.rooms);
      const selectedRun =
        runs.find((r) => r.id === selectedWiringRunId) || null;
      const { positions: screenPositions, handles: wireHandles } =
        drawElectricalOverlay(
          ctx,
          allObjects,
          allElectricalData,
          scale,
          pan,
          worldToScreen,
          {
            wires: showWiring,
            // The Wires tool rings what it can still wire, once a switch is picked.
            highlightTargets: !!connectionMode && !!connectionStart,
            startId: connectionStart?.id,
            selectedConnectionIds: selectedRun
              ? new Set(selectedRun.connections.map((c) => c.id))
              : null,
            visibleIds,
            // Runs, so each wire can bow away from its own run's anchor.
            runs,
            // A handle drag in flight, so the wire follows the pointer.
            shapeDrafts: wireShapeDrafts,
          }
        );

      // Publish exactly what was drawn for the wire hit test — positions and
      // runs together, so a click is measured against this frame's geometry.
      if (wiringHitRef) {
        wiringHitRef.current = {
          screenPositions,
          // The grab handles on the selected run, for the drag hit test.
          wireHandles,
          scale,
          runs: showWiring ? runs : [],
          visibleIds,
        };
      }

      // Highlight connection start object when in connection mode
      if (connectionMode && connectionStart && connectionStart.center) {
        const startPos = worldToScreen(
          connectionStart.center.x,
          connectionStart.center.z
        );
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.arc(startPos.x, startPos.y, 20, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      ctx.restore(); // Restore the world coordinate transform
    }

    // Inspected electrical — the outlets, switches, lights and fans the
    // existing-conditions pass found. Read-only marks, in screen space through
    // the same door as the wiring, scoped to the rooms being drawn so the floor
    // switcher and room isolation narrow it like everything else. Drawn before
    // the callouts, which stay on top of every layer.
    if (inspectedElectrical?.length) {
      const worldToScreen = beginScreenSpaceOverlay(ctx, {
        width,
        height,
        pan,
        scale,
        rotation,
      });
      drawInspectedElectricalOverlay(
        ctx,
        devicesInRooms(inspectedElectrical, dataToRender?.rooms),
        worldToScreen
      );
      ctx.restore();
    }

    // Callouts — notes with a leader pointing at what they are about.
    //
    // Last, and on top of every other layer: an annotation something else
    // paints over is not an annotation. Screen space through the same one
    // door the wiring uses, because the cloud and its type are sized in
    // PIXELS — text is a paper-space property, and a note that shrank with
    // the zoom would be unreadable at the zoom where you want one.
    //
    // Scoped to the rooms being drawn, so isolating a room hides the notes
    // belonging to the others. A note has exactly one owner, so unlike a
    // wiring run there is no run-spans-two-rooms case to carve out.
    if (showCallouts) {
      const worldToScreen = beginScreenSpaceOverlay(ctx, {
        width,
        height,
        pan,
        scale,
        rotation,
      });

      const calloutHits = drawCalloutsOverlay(
        ctx,
        dataToRender?.rooms,
        worldToScreen,
        { selectedId: selectedCalloutId }
      );

      // The half-placed note: a dashed leader from the anchor to the cursor,
      // so the cloud's position is visible before the click that commits it.
      if (calloutDrawing?.isActive && calloutDrawing.anchor) {
        drawCalloutPreview(
          ctx,
          calloutDrawing.anchor,
          calloutDrawing.cursorPos || calloutDrawing.anchor,
          worldToScreen
        );
      }

      // Published for the hit test, so a click is answered by the geometry
      // this frame actually drew — the same contract as `wiringHitRef`.
      if (calloutHitRef) calloutHitRef.current = calloutHits;
      ctx.restore();
    } else if (calloutHitRef) {
      // Nothing drawn means nothing clickable; a stale ref would keep
      // answering clicks for notes that are no longer on screen.
      calloutHitRef.current = [];
    }

    // Stretch preview overlay — during preview the canvas already shows
    // the post-stretch structure. Highlight the walls whose length
    // changed by drawing dashed orange lines on top of their new
    // positions, so the user can pick them out from the unchanged walls.
    // Plus draw a faint gray ghost of the OLD position of every wall
    // that moved or stretched, so the user can see where the boundary
    // line (which lives in original coordinates) sits relative to the
    // pre-stretch geometry.
    if (prePreviewStructure && Array.isArray(structureData?.walls)) {
      const beforeById = new Map();
      (prePreviewStructure.walls || []).forEach((w) => {
        if (w && w.id) beforeById.set(w.id, w);
      });

      const drawWallSegment = (w) => {
        if (!w?.center || !w?.dimensions || !w?.rotation) return;
        const halfL = (w.dimensions.x || 0) / 2;
        const ry = w.rotation.y || 0;
        const dx = Math.cos(ry);
        const dz = -Math.sin(ry);
        ctx.beginPath();
        ctx.moveTo(w.center.x - dx * halfL, w.center.z - dz * halfL);
        ctx.lineTo(w.center.x + dx * halfL, w.center.z + dz * halfL);
        ctx.stroke();
      };

      // Light-purple ghost of the OLD room + exterior walls (the
      // interior and exterior faces the user actually sees in the
      // floor plan, inset from the structure-wall centerline by
      // thickness/2). Anchors the boundary slider — which lives in
      // pre-stretch coordinates — to the structure as the user
      // remembered it. Same hue as the boundary slider so the eye
      // links them.
      const newRoomWallById = new Map();
      (structureData.rooms || []).forEach((room) => {
        (room.walls || []).forEach((w) => {
          if (w?.id) newRoomWallById.set(w.id, w);
        });
      });
      const newExteriorWallById = new Map();
      const collectExteriorWalls = (s) => s?.exterior?.walls || [];
      collectExteriorWalls(structureData).forEach((w) => {
        if (w?.id) newExteriorWallById.set(w.id, w);
      });
      const drawGhostIfMoved = (before, after) => {
        if (!before?.id || !before?.center || !after?.center) return;
        const moved =
          Math.abs((after.center.x || 0) - (before.center.x || 0)) > 1e-4 ||
          Math.abs((after.center.z || 0) - (before.center.z || 0)) > 1e-4 ||
          Math.abs((after.dimensions?.x || 0) - (before.dimensions?.x || 0)) >
            1e-4;
        if (moved) drawWallSegment(before);
      };
      ctx.save();
      ctx.strokeStyle = STRETCH_PREVIEW_COLORS.ghost;
      ctx.lineWidth = Math.max(0.02, 1.5 / scale);
      (prePreviewStructure.rooms || []).forEach((room) => {
        (room.walls || []).forEach((before) => {
          drawGhostIfMoved(before, newRoomWallById.get(before?.id));
        });
      });
      collectExteriorWalls(prePreviewStructure).forEach((before) => {
        drawGhostIfMoved(before, newExteriorWallById.get(before?.id));
      });
      ctx.restore();

      // Orange dashed overlay on the NEW position for length-changed walls.
      ctx.save();
      ctx.strokeStyle = STRETCH_PREVIEW_COLORS.resized;
      ctx.lineWidth = Math.max(0.03, 3 / scale);
      ctx.setLineDash([0.15, 0.1]);
      structureData.walls.forEach((w) => {
        if (!w?.id) return;
        const before = beforeById.get(w.id);
        if (!before) return;
        const lengthChanged =
          Math.abs((w.dimensions?.x || 0) - (before.dimensions?.x || 0)) > 1e-4;
        if (lengthChanged) drawWallSegment(w);
      });
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Stretch translate-rectangle (everything inside translates, outside
    // stays). Purple, so it's distinct from the orange "changed" overlay and
    // red collisions. The near edge is never set without the other three
    // (useStretchPlan seeds all four together), so there is no line-only case.
    if (boundaryAxis?.dir) {
      const dir = boundaryAxis.dir;
      const perp = { x: -dir.z, z: dir.x };

      if (stretchRegion) {
        const { near, far, min, max } = stretchRegion;
        const toWorld = (d, p) => ({
          x: dir.x * d + perp.x * p,
          z: dir.z * d + perp.z * p,
        });
        const corners = [
          toWorld(near, min),
          toWorld(far, min),
          toWorld(far, max),
          toWorld(near, max),
        ];
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].z);
        for (let i = 1; i < corners.length; i++) {
          ctx.lineTo(corners[i].x, corners[i].z);
        }
        ctx.closePath();
        // Faint fill so the enclosed region reads as "this part moves".
        ctx.fillStyle = STRETCH_PREVIEW_COLORS.regionFill;
        ctx.fill();
        ctx.strokeStyle = STRETCH_PREVIEW_COLORS.region;
        ctx.lineWidth = Math.max(0.04, 3 / scale);
        ctx.setLineDash([0.2, 0.15]);
        ctx.stroke();
        ctx.setLineDash([]);
        // Round grab tab at each edge midpoint so dragging is discoverable.
        const r = Math.max(0.06, 5 / scale);
        const mids = [
          toWorld((near + far) / 2, min),
          toWorld((near + far) / 2, max),
          toWorld(near, (min + max) / 2),
          toWorld(far, (min + max) / 2),
        ];
        ctx.fillStyle = STRETCH_PREVIEW_COLORS.region;
        for (const m of mids) {
          ctx.beginPath();
          ctx.arc(m.x, m.z, r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // Clamp-reason label: when the near edge just snapped out of a
      // Δ-dependent forbidden zone, render a small purple pill adjacent to
      // it, anchored next to the edited wall so it stays on-screen.
      if (
        stretchBoundaryClampReason &&
        boundaryAxis.anchor &&
        boundaryAxis.mover &&
        stretchBoundary != null
      ) {
        const midAnchor = {
          x: (boundaryAxis.anchor.x + boundaryAxis.mover.x) / 2,
          z: (boundaryAxis.anchor.z + boundaryAxis.mover.z) / 2,
        };
        const perpAtMid = midAnchor.x * perp.x + midAnchor.z * perp.z;
        // Anchor the label on the moving side of the line so it
        // doesn't sit on top of the dashed line.
        const labelOffsetWorld = 0.25; // 25 cm in world units
        const lx =
          dir.x * stretchBoundary +
          perp.x * perpAtMid +
          dir.x * labelOffsetWorld;
        const lz =
          dir.z * stretchBoundary +
          perp.z * perpAtMid +
          dir.z * labelOffsetWorld;

        const lenLabel = metersToFeetInches(
          stretchBoundaryClampReason.currentLength
        );
        const text = `Wall is ${lenLabel} — would collapse`;

        ctx.save();
        const px2 = (n) => n / scale;
        ctx.font = `${px2(13)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const tw = ctx.measureText(text).width;
        const padX = px2(8);
        const padY = px2(5);
        ctx.fillStyle = 'rgba(243, 232, 255, 0.96)'; // pale purple
        ctx.strokeStyle = '#7c3aed'; // matches the boundary line
        ctx.lineWidth = px2(1);
        ctx.beginPath();
        ctx.rect(
          lx - tw / 2 - padX,
          lz - px2(10),
          tw + 2 * padX,
          px2(20) + padY
        );
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#5b21b6';
        ctx.fillText(text, lx, lz + px2(2));
        ctx.restore();
      }
    }

    // Collision highlights — render any wall or fixture in `collisionTargets`
    // with a thick red outline so the user sees what's blocking the edit.
    if (collisionTargets) {
      ctx.save();
      ctx.strokeStyle = '#e53935'; // HIGHLIGHT_COLORS.ERROR
      ctx.lineWidth = Math.max(0.05, 4 / scale);
      ctx.fillStyle = 'rgba(229, 57, 53, 0.25)';

      // Walls — match against both structure-level and room-level
      // wall arrays. Expansion-collision errors point to structure
      // walls; locked-conflict errors point to room walls (lock keys
      // resolve to room wall ids). One Set covers both — the painter
      // walks each array and renders any id found.
      if (collisionTargets.walls && collisionTargets.walls.size > 0) {
        const drawnWallIds = new Set();
        const drawWall = (w) => {
          if (
            !w?.id ||
            !collisionTargets.walls.has(w.id) ||
            drawnWallIds.has(w.id)
          )
            return;
          if (!w.center || !w.dimensions || !w.rotation) return;
          drawnWallIds.add(w.id);
          const halfL = (w.dimensions.x || 0) / 2;
          const ry = w.rotation.y || 0;
          const dx = Math.cos(ry);
          const dz = -Math.sin(ry);
          ctx.beginPath();
          ctx.moveTo(w.center.x - dx * halfL, w.center.z - dz * halfL);
          ctx.lineTo(w.center.x + dx * halfL, w.center.z + dz * halfL);
          ctx.stroke();
        };
        (structureData?.walls || []).forEach(drawWall);
        (structureData?.rooms || []).forEach((room) => {
          (room.walls || []).forEach(drawWall);
        });
      }

      // Fixtures (free-standing objects)
      if (collisionTargets.fixtures && collisionTargets.fixtures.size > 0) {
        const drawnIds = new Set();
        const drawFixture = (fix) => {
          if (
            !fix?.id ||
            !collisionTargets.fixtures.has(fix.id) ||
            drawnIds.has(fix.id) ||
            !fix.center ||
            !fix.dimensions
          ) {
            return;
          }
          drawnIds.add(fix.id);
          const halfX = (fix.dimensions.x || 0) / 2;
          const halfZ = (fix.dimensions.z || 0) / 2;
          const ry = fix.rotation?.y || 0;
          const cosR = Math.cos(ry);
          const sinR = Math.sin(ry);
          const corners = [
            { x: -halfX, z: -halfZ },
            { x: halfX, z: -halfZ },
            { x: halfX, z: halfZ },
            { x: -halfX, z: halfZ },
          ].map((c) => ({
            x: fix.center.x + c.x * cosR - c.z * sinR,
            z: fix.center.z + c.x * sinR + c.z * cosR,
          }));
          ctx.beginPath();
          corners.forEach((c, idx) => {
            if (idx === 0) ctx.moveTo(c.x, c.z);
            else ctx.lineTo(c.x, c.z);
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        };
        (structureData?.rooms || []).forEach((room) => {
          (room.objects || []).forEach(drawFixture);
        });
        (structureData?.objects || []).forEach(drawFixture);
      }

      ctx.restore();
    }

    // Overlap warnings — fixtures that translated into another wall's
    // footprint after a shrink. Yellow outline + light fill, distinct
    // from the red collision highlights (which block) — these are
    // informational only.
    if (overlapWarningTargets?.fixtures?.size > 0) {
      ctx.save();
      ctx.strokeStyle = '#f59e0b'; // amber-500
      ctx.lineWidth = Math.max(0.05, 4 / scale);
      // Stroke-only outline (no fill) at the new fixture position.
      // One rectangle per fixture, drawn from whichever array we hit
      // first — beds and other objects often appear in BOTH
      // structure.objects and room.objects, so we dedupe by id.
      const drawnIds = new Set();
      const drawWarning = (fix) => {
        if (
          !fix?.id ||
          !overlapWarningTargets.fixtures.has(fix.id) ||
          drawnIds.has(fix.id) ||
          !fix.center ||
          !fix.dimensions
        ) {
          return;
        }
        drawnIds.add(fix.id);
        // Match drawObject (elementDrawingUtils.drawObject + drawObjectShape):
        // - objectAngle is derived from full 3D rotation projected to XZ
        //   via apply3DRotationTo2D, NOT from rotation.y alone. The
        //   handedness of the Y-axis rotation matters: for non-axis
        //   angles, using rotation.y directly with a CCW 2D matrix
        //   produces a rectangle mirrored on the x-axis vs the actual
        //   render.
        // - drawObjectShape draws rect(-depth/2, -width/2, depth, width)
        //   in local space — local x = dim.z, local y = dim.x.
        const dir = apply3DRotationTo2D(fix.rotation, [0, 0, 1]);
        const objectAngle = Math.atan2(dir[1], dir[0]);
        ctx.save();
        ctx.translate(fix.center.x, fix.center.z);
        ctx.rotate(objectAngle);
        const hd = (fix.dimensions.z || 0) / 2;
        const hw = (fix.dimensions.x || 0) / 2;
        ctx.beginPath();
        ctx.rect(-hd, -hw, hd * 2, hw * 2);
        ctx.stroke();
        ctx.restore();
      };
      // Prefer room-level entries (the canonical render source) so
      // the outline tracks the rendered fixture exactly. Walk every
      // child kind — doors, windows, openings, objects — at room,
      // structure, and exterior level. Without doors/windows/openings
      // here, a door that triggers a fixture-overlap warning would
      // leave no visible outline even though the panel calls it out.
      (structureData?.rooms || []).forEach((room) => {
        (room.doors || []).forEach(drawWarning);
        (room.windows || []).forEach(drawWarning);
        (room.openings || []).forEach(drawWarning);
        (room.objects || []).forEach(drawWarning);
      });
      (structureData?.doors || []).forEach(drawWarning);
      (structureData?.windows || []).forEach(drawWarning);
      (structureData?.openings || []).forEach(drawWarning);
      (structureData?.objects || []).forEach(drawWarning);
      const ext = structureData?.exterior;
      if (ext) {
        (ext.doors || []).forEach(drawWarning);
        (ext.windows || []).forEach(drawWarning);
        (ext.openings || []).forEach(drawWarning);
      }
      ctx.restore();
    }

    // Restore the original transformation state
    restoreCanvasTransform(ctx);

    // Draw axes in screen space (after restoring transform) - only in debug mode
    if (debugMode) {
      const axisDpr = window.devicePixelRatio || 1;
      drawAxes(ctx, {
        width: canvas.width / axisDpr,
        height: canvas.height / axisDpr,
      });
    }
  }, [
    structureData,
    structureData?.dataVersion, // Force redraw when data is refreshed
    structureData?.rooms, // Force redraw when rooms change
    forceRedrawCounter, // Force redraw when refreshData event is received
    scale,
    pan,
    selectedItem,
    showRoomLabels,
    showObjects,
    shouldShowMeasurements,
    rotation,
    selectedRoom,
    canvasSize, // Add canvasSize as dependency
    debugMode,
    measurementMode,
    dataSource, // Add dataSource as dependency
    previewStructure, // Add previewStructure to redraw when toggling preview mode
    prePreviewStructure, // Add prePreviewStructure to redraw orange overlay when entering/leaving preview
    stretchBoundary, // Redraw the boundary line when user drags it
    boundaryAxis, // Redraw the boundary line when the edited measurement changes
    stretchBoundaryClampReason, // Redraw the clamp-reason label when it appears/changes
    previewMap, // Add previewMap to refresh affected labels when plan changes
    lockedKeys, // Add lockedKeys to refresh lock-glyph rendering
    collisionTargets, // Add collisionTargets to redraw red highlights
    overlapWarningTargets, // Redraw yellow halos for post-shrink overlaps
    showFixtures, // Add showFixtures as dependency
    showElectrical, // Add showElectrical as dependency
    showFurnishings, // Add showFurnishings as dependency
    showStaging,
    showDemoedWalls, // Redraw when demoed-wall visibility toggles
    tempResizedObject, // Add to trigger redraw during resize
    isResizing, // Add to trigger redraw when resize state changes
    activeResizeHandle, // Add to update handle highlighting
    // Removed tempDraggedObject dependency - using direct updates
    isDraggingObject, // Add to trigger redraw when drag state changes
    // Removed tempEditedWall dependency - using direct updates
    isDraggingWall, // Add to trigger redraw when wall drag state changes
    previewObject, // Add to trigger redraw when preview object changes
    electricalMode, // Add to trigger redraw when electrical mode changes
    showDimensionsOnly,
    wireShapeDrafts, // redraw as a wire's handle is dragged
    selectedWiringRunId, // redraw so the picked run highlights/unhighlights
    showCallouts, // Redraw when the note layer's admin gate flips
    inspectedElectrical, // Redraw when the inspected-electrical layer flips or loads
    selectedCalloutId, // redraw so the picked note highlights/unhighlights
    showWiring, // Redraw when the Wiring toggle flips
    connectionMode, // Add to trigger redraw when connection mode changes
    selectedFloorPlanMeasurement, // Add to trigger redraw when measurement selection changes
    connectionStart, // Add to trigger redraw when connection start changes
    showCollisionIndicator, // Re-mark collisions when this admin gate flips
    takeOffOverlay, // Re-render when room-info-panel "Show" toggles
    roomHighlightTick, // Animate the room-selection fade-out
    appliancePreview, // Repaint the ghosted appliance-drop preview as it moves
  ]);
  // === END VERBATIM ===
}
